import React, { useEffect, useMemo, useRef, useState } from "react"
import { useClientsSearch } from "@/hooks/use-clients-search"
import { Link } from "react-router-dom"
import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query"
import { SiteHeader } from "@/components/site-header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Pagination } from "@/components/ui/pagination"
import { authorizedFetch } from "@/lib/api"
import { usePermissions } from "@/hooks/use-permissions"
import { cn } from "@/lib/utils"
import {
  SearchIcon,
  UploadIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DownloadIcon,
  EyeIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowUpDownIcon,
  XIcon,
  PencilIcon,
  Trash2Icon,
  ExternalLinkIcon,
  AlertTriangleIcon,
  RefreshCwIcon,
  PackageIcon,
  TruckIcon,
  FileTextIcon,
  CalculatorIcon,
  SlidersHorizontalIcon,
  CalendarIcon,
  PlusIcon,
} from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet"
import { Label } from "@/components/ui/label"
import { Progress } from "@/components/ui/progress"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { isCDCFleteroPrefix, isFleteroOwner } from "@cdc-sccr/shared"
import { ConnectCrtsDialog } from "./ConnectCrtsDialog"
import type { ConnectCrtPreview } from "./ConnectCrtsDialog"
import { UnitsModal } from "./UnitsModal"
import { CargoProductsModal } from "./CargoProductsModal"
import { DriversModal } from "./DriversModal"
import { ClientsModal } from "./ClientsModal"
import { QuickRouteDialog } from "./QuickRouteDialog"
import type { QuickRouteTarget } from "./QuickRouteDialog"
import { FormRow, CityField, Field, SheetSearchSelect, FilterCombobox, SortIcon } from "./SmartGTComponents"
import type { CrtRow, DetailRow, CrtFullRow, ImportResult, HeaderValidationResult, CrtsResponse, CreateCrtForm, ExportRules, SortCol } from "./types"
import { EMPTY_CREATE } from "./types"
import { fmtDate, fmtDatetime, isoToLocal, buildDetailPayload } from "./utils"

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3001"
const PAGE_SIZE_OPTIONS = [25, 50, 100]


// ─── Page ──────────────────────────────────────────────────────────────────────────────

// ────────────────────────────────────────────────────────────────────────────

export default function SmartGTPage() {
  const qc = useQueryClient()
  const { can } = usePermissions()

  // ── Upload state ───────────────────────────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [pendingFile, setPendingFile]   = useState<string | null>(null)
  const [uploadError, setUploadError]   = useState<string | null>(null)
  const [warningsOpen,   setWarningsOpen]   = useState(false)
  const [errorsOpen,     setErrorsOpen]     = useState(false)
  const [headerDiffOpen, setHeaderDiffOpen] = useState(false)
  const [formatVersion,  setFormatVersion]  = useState<"v1" | "v2">("v2")
  const [pendingImportFile, setPendingImportFile] = useState<File | null>(null)
  const [headerValidation, setHeaderValidation] = useState<HeaderValidationResult | null>(null)

  // ── Filter + pagination state ──────────────────────────────────────────────
  const [search, setSearch]               = useState("")
  const [client, setClient]               = useState("")
  const [driver, setDriver]               = useState("")
  // carrier state removed — filter hidden from UI
  const [statusConfig, setStatusConfig]   = useState("")
  const [crtStatusFilter, setCrtStatusFilter] = useState("Abierto")
  const [origin, setOrigin]               = useState("")
  const [destination, setDestination]     = useState("")
  const [collectionArrivalFrom, setCollectionArrivalFrom] = useState("")
  const [collectionArrivalTo,   setCollectionArrivalTo]   = useState("")
  const [page,     setPage]     = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [sortBy, setSortBy]   = useState<SortCol | null>("collection_arrival")
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc")

  // ── Expand state ───────────────────────────────────────────────────────────
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [expandedDetails, setExpandedDetails] = useState<DetailRow[] | null>(null)
  const [loadingExpand, setLoadingExpand] = useState<string | null>(null)
  const [detailModal, setDetailModal] = useState<DetailRow | null>(null)
  const [crtViewModal, setCrtViewModal] = useState<CrtFullRow | null>(null)
  const [loadingCrtView, setLoadingCrtView] = useState<string | null>(null)

  // ── Edit / delete state ────────────────────────────────────────────────────
  // CRT create (manual — "Nuevo CRT" sheet)
  const [creatingCrt, setCreatingCrt]       = useState(false)
  const [createCrtForm, setCreateCrtForm]   = useState<CreateCrtForm>(EMPTY_CREATE)
  const [createCrtError, setCreateCrtError] = useState<string | null>(null)
  // CRT connect — "Conexión entre destinos" dialog
  const [connectOpen, setConnectOpen]         = useState(false)
  const [connectSwapped, setConnectSwapped]   = useState(false)
  const [connectError, setConnectError]       = useState<string | null>(null)
  // CRT delete
  const [deletingCrt, setDeletingCrt] = useState<CrtRow | null>(null)
  // CRT edit
  const [editingCrt, setEditingCrt]   = useState<CrtRow | null>(null)
  const [crtForm, setCrtForm]         = useState<Record<string, string>>({})
  const [crtFormError, setCrtFormError] = useState<string | null>(null)
  // Detail delete
  const [deletingDetail, setDeletingDetail] = useState<{ id: string; sourceId: string; crtId: string } | null>(null)
  // Units modal (truck / trailer)
  const [unitsModal, setUnitsModal] = useState<{ plate: string; type: "truck" | "trailer" } | null>(null)
  const [productsModal, setProductsModal] = useState<{ name: string } | null>(null)
  const [driversModal, setDriversModal]   = useState<{ name: string } | null>(null)
  const [clientsModal, setClientsModal]   = useState<{ name: string } | null>(null)

  // ── Quick-create route dialog ──────────────────────────────────────────
  const [quickRouteTarget, setQuickRouteTarget] = useState<QuickRouteTarget | null>(null)

  // Detail edit
  const [editingDetail, setEditingDetail]   = useState<DetailRow | null>(null)
  const [detailForm, setDetailForm]         = useState<Record<string, string>>({})
  const [detailFormError, setDetailFormError] = useState<string | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [exportPreview, setExportPreview] = useState<{ csvText: string; filename: string; endpoint: string } | null>(null)

  // ── Export rules — persisted in localStorage ──────────────────────────────
  const [exportRules, setExportRules] = useState<ExportRules>(() => {
    try {
      const stored = localStorage.getItem("sccr:exportRules")
      if (stored) {
        const parsed = JSON.parse(stored) as Partial<ExportRules>
        return {
          cargoData: { groupByCrt: parsed.cargoData?.groupByCrt ?? true },
          tripData: {
            mergeByDriver:    parsed.tripData?.mergeByDriver    ?? true,
            mergeMaxDaysDiff: parsed.tripData?.mergeMaxDaysDiff ?? 2,
            replaceTJClient:  parsed.tripData?.replaceTJClient  ?? true,
            applyFleteroRules: parsed.tripData?.applyFleteroRules ?? true,
          },
        }
      }
    } catch { /* ignore */ }
    return {
      cargoData: { groupByCrt: true },
      tripData:  { mergeByDriver: true, mergeMaxDaysDiff: 2, replaceTJClient: true, applyFleteroRules: true },
    }
  })
  const [rulesOpen, setRulesOpen] = useState(false)

  useEffect(() => {
    localStorage.setItem("sccr:exportRules", JSON.stringify(exportRules))
  }, [exportRules])

  useEffect(() => {
    setPage(1)
  }, [search, client, driver, statusConfig, crtStatusFilter, origin, destination, collectionArrivalFrom, collectionArrivalTo])

  function handleSort(col: SortCol) {
    if (sortBy === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortBy(col)
      // Warnings default to desc (most problems first)
      setSortDir(col === "warning_count" ? "desc" : "asc")
    }
  }

  // ── Filter options (derived from full dataset) ─────────────────────────────
  // ── Filter options from dedicated endpoint ────────────────────────────────────────
  const { data: filterOptions } = useQuery({
    queryKey: ["smart-gt-filter-options"],
    queryFn: async () => {
      const res = await authorizedFetch(`${API_URL}/api/smart-gt/filter-options`)
      return res.json() as Promise<{
        products: string[]
        carriers: string[]
        statusConfigs: string[]
        borderCrossings: string[]
        origins: string[]
        destinations: string[]
      }>
    },
    staleTime: 5 * 60 * 1000,
  })

  // ── CRT list (server-side paginated) ──────────────────────────────────────
  const { data: crtsData, isLoading } = useQuery({
    queryKey: [
      "smart-gt-crts",
      page, pageSize, search, client, driver,
      statusConfig, crtStatusFilter, origin, destination,
      collectionArrivalFrom, collectionArrivalTo,
      sortBy, sortDir,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        page:     String(page),
        pageSize: String(pageSize),
      })
      if (search)                   params.set("search",                search)
      if (client)                   params.set("client",                client)
      if (driver)                   params.set("driver",                driver)
      if (collectionArrivalFrom) params.set("collectionArrivalFrom", collectionArrivalFrom)
      if (collectionArrivalTo)   params.set("collectionArrivalTo",   collectionArrivalTo)
      if (statusConfig)      params.set("statusConfig",   statusConfig)
      if (crtStatusFilter)   params.set("crtStatus",      crtStatusFilter)
      if (origin)            params.set("origin",         origin)
      if (destination)       params.set("destination",    destination)
      if (sortBy)            params.set("sortBy",         sortBy)
      if (sortDir)           params.set("sortDir",        sortDir)
      const res = await authorizedFetch(
        `${API_URL}/api/smart-gt/crts?${params}`
      )
      return res.json() as Promise<CrtsResponse>
    },
    placeholderData: keepPreviousData,
  })

  // ── Recalculate warnings ──────────────────────────────────────────────────
  const recalcMut = useMutation({
    mutationFn: () => authorizedFetch(`${API_URL}/api/smart-gt/recalculate-warnings`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["smart-gt-crts"] }),
    onError: (err: Error) => alert(err.message),
  })

  // ── Validate headers mutation (no DB write) ───────────────────────────────
  const validateHeadersMut = useMutation<HeaderValidationResult, Error, File>({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append("file", file)
      formData.append("version", formatVersion)
      formData.append("validate_only", "true")
      const res = await authorizedFetch(`${API_URL}/api/smart-gt/import`, {
        method: "POST",
        body: formData,
      })
      return res.json()
    },
    onSuccess: (result, file) => {
      setPendingImportFile(file)
      setHeaderValidation(result)
    },
    onError: (err: Error) => setUploadError(err.message),
  })

  // ── Upload mutation ──────────────────────────────────────────────────────────
  const importMut = useMutation<ImportResult, Error, File>({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append("file", file)
      formData.append("version", formatVersion)
      const res = await authorizedFetch(`${API_URL}/api/smart-gt/import`, {
        method: "POST",
        body: formData,
      })
      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["smart-gt-crts"] })
      qc.invalidateQueries({ queryKey: ["smart-gt-filter-options"] })
    },
    onError: (err: Error) => setUploadError(err.message),
  })

  // ── Delete CRT ────────────────────────────────────────────────────────────
  const deleteCrtMut = useMutation({
    mutationFn: (id: string) =>
      authorizedFetch(`${API_URL}/api/smart-gt/crts/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["smart-gt-crts"] })
      qc.invalidateQueries({ queryKey: ["smart-gt-filter-options"] })
      setDeletingCrt(null)
    },
    onError: (err: Error) => alert(err.message),
  })

  // ── Update CRT ────────────────────────────────────────────────────────────
  const updateCrtMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, string | null> }) =>
      authorizedFetch(`${API_URL}/api/smart-gt/crts/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["smart-gt-crts"] })
      qc.invalidateQueries({ queryKey: ["smart-gt-filter-options"] })
      setEditingCrt(null)
    },
    onError: (err: Error) => setCrtFormError(err.message),
  })

  // ── Select queries for Create CRT form ───────────────────────────────────────
  type ClientOption  = { id: string; name: string; sgtCodes: string[] | null }
  type CityOption    = { id: string; name: string; sgtCodes: string[] | null; stateName: string }
  type DriverOption  = { id: string; name: string; sgtCodes: string[] | null }

  const { clients: clientOptions } = useClientsSearch({ enabled: creatingCrt, onlyWithSgt: true })

  const { data: cityOptions = [] } = useQuery<CityOption[]>({
    queryKey: ["cities-for-select"],
    queryFn: async () => {
      const r = await authorizedFetch(`${API_URL}/api/geo/cities?deleted=false`)
      const data = await r.json() as CityOption[]
      return data.filter(c => c.sgtCodes && c.sgtCodes.length > 0)
    },
    enabled: creatingCrt,
    staleTime: 60_000,
  })

  const { data: driverOptions = [] } = useQuery<DriverOption[]>({
    queryKey: ["drivers-for-select"],
    queryFn: async () => {
      const r = await authorizedFetch(`${API_URL}/api/providers/drivers?deleted=false&pageSize=500`)
      const json = await r.json() as { data: DriverOption[] }
      return json.data.filter(d => d.sgtCodes && d.sgtCodes.length > 0)
    },
    enabled: creatingCrt,
    staleTime: 60_000,
  })

  // ── Create CRT ─────────────────────────────────────────────────────────────
  const createCrtMut = useMutation({
    mutationFn: (data: CreateCrtForm) =>
      authorizedFetch(`${API_URL}/api/smart-gt/crts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          crt:           data.crt.trim(),
          crtStatus:     data.crtStatus || "Abierto",
          client:        data.client   || null,
          shipperCity:   data.shipperCity   || null,
          recipientCity: data.recipientCity || null,
          driverName:    data.driverName    || null,
          truckPlate:    data.truckPlate    || null,
          trailerPlate:  data.trailerPlate  || null,
          loadingDate:   data.loadingDate   || null,
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["smart-gt-crts"] })
      qc.invalidateQueries({ queryKey: ["smart-gt-filter-options"] })
      setCreatingCrt(false)
      setCreateCrtForm(EMPTY_CREATE)
      setCreateCrtError(null)
    },
    onError: (err: Error) => setCreateCrtError(err.message),
  })

  // ── Connect CRTs (Conexión entre destinos) ──────────────────────────────────
  const connectCrtMut = useMutation({
    mutationFn: ({ firstCrtId, secondCrtId }: { firstCrtId: string; secondCrtId: string }) =>
      authorizedFetch(`${API_URL}/api/smart-gt/crts/connect`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ firstCrtId, secondCrtId }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["smart-gt-crts"] })
      qc.invalidateQueries({ queryKey: ["smart-gt-filter-options"] })
      setConnectOpen(false)
      setConnectSwapped(false)
      setConnectError(null)
      setSelectedIds(new Set())
    },
    onError: (err: Error) => setConnectError(err.message),
  })

  // ── Bulk delete ─────────────────────────────────────────────────────────────
  const [bulkDeleteOpen,  setBulkDeleteOpen]  = useState(false)
  const [bulkDeleteError, setBulkDeleteError] = useState<string | null>(null)
  const bulkDeleteMut = useMutation({
    mutationFn: (ids: string[]) =>
      authorizedFetch(`${API_URL}/api/smart-gt/crts/bulk`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["smart-gt-crts"] })
      setSelectedIds(new Set())
      setBulkDeleteOpen(false)
      setBulkDeleteError(null)
    },
    onError: (err: Error) => setBulkDeleteError(err.message),
  })

  // ── Bulk status change ─────────────────────────────────────────────────────
  const [bulkStatusError,   setBulkStatusError]   = useState<string | null>(null)
  const [bulkStatusPending, setBulkStatusPending] = useState<string | null>(null)
  const bulkStatusMut = useMutation({
    mutationFn: ({ ids, crtStatus }: { ids: string[]; crtStatus: string }) =>
      authorizedFetch(`${API_URL}/api/smart-gt/crts/bulk-status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids, crtStatus }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["smart-gt-crts"] })
      setSelectedIds(new Set())
      setBulkStatusError(null)
    },
    onError: (err: Error) => setBulkStatusError(err.message),
  })

  // ── Delete Detail ─────────────────────────────────────────────────────────
  const deleteDetailMut = useMutation({
    mutationFn: (id: string) =>
      authorizedFetch(`${API_URL}/api/smart-gt/details/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      if (expandedId) {
        authorizedFetch(`${API_URL}/api/smart-gt/crts/${expandedId}`)
          .then(r => r.json())
          .then((crtData: { crt: string; details: DetailRow[] }) => {
            const detailsWithCrt = (crtData.details ?? []).map(d => ({ ...d, crt: crtData.crt }))
            setExpandedDetails(detailsWithCrt)
          })
      }
      qc.invalidateQueries({ queryKey: ["smart-gt-crts"] })
      setDeletingDetail(null)
    },
    onError: (err: Error) => alert(err.message),
  })

  // ── Update Detail ─────────────────────────────────────────────────────────
  const updateDetailMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      authorizedFetch(`${API_URL}/api/smart-gt/details/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      if (expandedId) {
        authorizedFetch(`${API_URL}/api/smart-gt/crts/${expandedId}`)
          .then(r => r.json())
          .then((crtData: { crt: string; details: DetailRow[] }) => {
            const detailsWithCrt = (crtData.details ?? []).map(d => ({ ...d, crt: crtData.crt }))
            setExpandedDetails(detailsWithCrt)
          })
      }
      qc.invalidateQueries({ queryKey: ["smart-gt-crts"] })
      setEditingDetail(null)
    },
    onError: (err: Error) => setDetailFormError(err.message),
  })

  const ALLOWED_EXTS = ["csv", "txt", "xls", "xlsx"]
  const MAX_MB = 10

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    // Client-side validation before sending
    const ext = file.name.split(".").pop()?.toLowerCase() ?? ""
    if (!ALLOWED_EXTS.includes(ext)) {
      setUploadError(`Tipo de archivo no soportado (.${ext}). Usá .xls, .xlsx, .csv o .txt.`)
      if (fileInputRef.current) fileInputRef.current.value = ""
      return
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      setUploadError(`El archivo es demasiado grande (${(file.size / 1024 / 1024).toFixed(1)} MB). Máximo: ${MAX_MB} MB.`)
      if (fileInputRef.current) fileInputRef.current.value = ""
      return
    }

    setUploadError(null)
    setWarningsOpen(false)
    setErrorsOpen(false)
    setHeaderDiffOpen(false)
    setHeaderValidation(null)
    setPendingFile(file.name)
    validateHeadersMut.mutate(file)
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  // ── CRT view modal ──────────────────────────────────────────────────────
  async function openCrtView(id: string) {
    setLoadingCrtView(id)
    try {
      const res = await authorizedFetch(`${API_URL}/api/smart-gt/crts/${id}`)
      const data = (await res.json()) as CrtFullRow
      setCrtViewModal(data)
    } finally {
      setLoadingCrtView(null)
    }
  }

  // ── Expand / collapse ──────────────────────────────────────────────────────
  async function handleExpand(id: string) {
    if (expandedId === id) {
      setExpandedId(null)
      setExpandedDetails(null)
      return
    }
    setLoadingExpand(id)
    try {
      const res = await authorizedFetch(`${API_URL}/api/smart-gt/crts/${id}`)
      const crtData = (await res.json()) as { crt: string; details: DetailRow[] }
      setExpandedId(id)
      // Add CRT code to each detail for CDC detection
      const detailsWithCrt = (crtData.details ?? []).map(d => ({
        ...d,
        crt: crtData.crt
      }))
      setExpandedDetails(detailsWithCrt)
    } finally {
      setLoadingExpand(null)
    }
  }

  // ── Edit helpers ──────────────────────────────────────────────────────────
  function openEditCrt(row: CrtRow) {
    setCrtFormError(null)
    setCrtForm({
      borderCrossing:   row.borderCrossing   ?? "",
      shipperCity:      row.shipperCity      ?? "",
      shipperCountry:   row.shipperCountry   ?? "",
      recipientCity:    row.recipientCity    ?? "",
      recipientCountry: row.recipientCountry ?? "",
      product:          row.product          ?? "",
      carrier:          row.carrier          ?? "",
      freight:          row.freight          ?? "",
      crtStatus:        row.crtStatus,
      notes:            row.notes            ?? "",
    })
    setEditingCrt(row)
  }

  function openEditDetail(d: DetailRow) {
    setDetailFormError(null)
    setDetailForm({
      driverName:           d.driverName           ?? "",
      truckPlate:           d.truckPlate           ?? "",
      truckOwner:           d.truckOwner           ?? "",
      trailerPlate:         d.trailerPlate         ?? "",
      driverPhone:          d.driverPhone          ?? "",
      weight:               d.weight               ?? "",
      driverAdvanceTotal:   d.driverAdvanceTotal   ?? "",
      formula:              d.formula              ?? "",
      cargoPosition:        d.cargoPosition        ?? "",
      positionType:         d.positionType         ?? "",
      currentStatus:        d.currentStatus        ?? "",
      storageDays:          d.storageDays != null ? String(d.storageDays) : "",
      expectedDate:                   isoToLocal(d.expectedDate),
      loadingDate:                    isoToLocal(d.loadingDate),
      unloadingDate:                  isoToLocal(d.unloadingDate),
      collectionScheduleDate:         isoToLocal(d.collectionScheduleDate),
      collectionArrival:              isoToLocal(d.collectionArrival),
      collectionDeparture:            isoToLocal(d.collectionDeparture),
      expectedArrivalBorder:          isoToLocal(d.expectedArrivalBorder),
      passwordAnticipation:           isoToLocal(d.passwordAnticipation),
      borderArrival:                  isoToLocal(d.borderArrival),
      authorizedCrossing:             isoToLocal(d.authorizedCrossing),
      passwordRequest:                isoToLocal(d.passwordRequest),
      customsOriginEntry:             isoToLocal(d.customsOriginEntry),
      customsOriginExit:              isoToLocal(d.customsOriginExit),
      customsTransitEntry:            isoToLocal(d.customsTransitEntry),
      customsTransitExit:             isoToLocal(d.customsTransitExit),
      expectedCustomsDestination:     isoToLocal(d.expectedCustomsDestination),
      customsDestinationEntry:        isoToLocal(d.customsDestinationEntry),
      customsDestinationExit:         isoToLocal(d.customsDestinationExit),
      expectedUnloadingArrival:       isoToLocal(d.expectedUnloadingArrival),
      unloadingDeparture:             isoToLocal(d.unloadingDeparture),
      unloadingArrival:               isoToLocal(d.unloadingArrival),
    })
    setEditingDetail(d)
  }

  const rows = crtsData?.data ?? []
  const total = crtsData?.total ?? 0
  const totalPages = crtsData?.totalPages ?? 1

  // ── Derived state for "Conexión entre destinos" button ────────────────────
  // Default order: oldest CRT (earliest collectionArrival) first, newest second.
  // The swap button lets the user override this automatic ordering.
  const connectPairOrdered = useMemo<[ConnectCrtPreview, ConnectCrtPreview] | [undefined, undefined]>(() => {
    const pair = rows.filter(r => selectedIds.has(r.id))
    // Sort by collectionArrival ascending (nulls last) to get oldest first
    const sorted = [...pair].sort((a, b) => {
      if (!a.collectionArrival && !b.collectionArrival) return 0
      if (!a.collectionArrival) return 1
      if (!b.collectionArrival) return -1
      return new Date(a.collectionArrival).getTime() - new Date(b.collectionArrival).getTime()
    })
    const first  = connectSwapped ? sorted[1] : sorted[0]
    const second = connectSwapped ? sorted[0] : sorted[1]
    if (!first || !second) return [undefined, undefined]
    // Resolve city fallbacks so the dialog shows (and the API stores) the effective city.
    // recipientCity can be null while unloadingCity has the value, and vice-versa for shipper/loading.
    return [
      {
        id:                first.id,
        crt:               first.crt,
        shipperCity:       first.shipperCity   ?? first.loadingCity   ?? null,
        recipientCity:     first.recipientCity ?? first.unloadingCity ?? null,
        collectionArrival: first.collectionArrival,
      },
      {
        id:                second.id,
        crt:               second.crt,
        shipperCity:       second.shipperCity   ?? second.loadingCity   ?? null,
        recipientCity:     second.recipientCity ?? second.unloadingCity ?? null,
        collectionArrival: second.collectionArrival,
      },
    ]
  }, [rows, selectedIds, connectSwapped])
  const [connectFirst, connectSecond] = connectPairOrdered

  // 13 columns: ☐ | ⚠ | CRT | Producto | Remitente | Destinatario | KM | Cliente | Estado | Viaje | Lleg. | Detalles | acciones
  const COL_COUNT = 13

  const allSelected = rows.length > 0 && selectedIds.size === rows.length
  const someSelected = selectedIds.size > 0 && !allSelected

  const hasWarningsSelected = useMemo(() =>
    rows.some(r => selectedIds.has(r.id) && (r.warningCount ?? 0) > 0),
    [rows, selectedIds]
  )

  const problemsSelectedCount = useMemo(() =>
    rows.reduce((acc, r) => acc + (selectedIds.has(r.id) && (r.warningCount ?? 0) > 0 ? 1 : 0), 0),
    [rows, selectedIds]
  )

  function toggleSelectAll() {
    setSelectedIds(allSelected ? new Set() : new Set(rows.map(r => r.id)))
  }

  function toggleSelect(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  async function handleExport(endpoint: "cargo-data" | "trip-data" | "cargo-documents" | "valuation") {
    try {
      const res = await authorizedFetch(
        `${API_URL}/api/smart-gt/export/${endpoint}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ids: [...selectedIds],
            options: {
              cargoData: { groupByCrt: exportRules.cargoData.groupByCrt },
              tripData:  {
                mergeByDriver:     exportRules.tripData.mergeByDriver,
                mergeMaxDaysDiff:  exportRules.tripData.mergeMaxDaysDiff,
                replaceTJClient:   exportRules.tripData.replaceTJClient,
                applyFleteroRules: exportRules.tripData.applyFleteroRules,
              },
            },
          }),
        },
      )
      const disposition = res.headers.get("Content-Disposition") ?? ""
      const match       = disposition.match(/filename="([^"]+)"/)
      const filename    = match?.[1] ?? `${endpoint}-export.csv`
      const csvText = await res.text()
      setExportPreview({ csvText, filename, endpoint })
    } catch (err) {
      console.error("Export failed", err)
    }
  }

  async function handleExportFullList() {
    try {
      const params = new URLSearchParams()
      if (search)                params.set("search",                search)
      if (client)                params.set("client",                client)
      if (driver)                params.set("driver",                driver)
      if (statusConfig)          params.set("statusConfig",          statusConfig)
      if (crtStatusFilter)       params.set("crtStatus",             crtStatusFilter)
      if (origin)                params.set("origin",                origin)
      if (destination)           params.set("destination",           destination)
      if (collectionArrivalFrom) params.set("collectionArrivalFrom", collectionArrivalFrom)
      if (collectionArrivalTo)   params.set("collectionArrivalTo",   collectionArrivalTo)
      const res = await authorizedFetch(
        `${API_URL}/api/smart-gt/export/full-list?${params}`,
      )
      const disposition = res.headers.get("Content-Disposition") ?? ""
      const match       = disposition.match(/filename="([^"]+)"/)
      const filename    = match?.[1] ?? "crt-full-list.csv"
      const csvText     = await res.text()
      setExportPreview({ csvText, filename, endpoint: "full-list" as any })
    } catch (err) {
      console.error("Export full-list failed", err)
    }
  }

  function handleDownloadPreview() {
    if (!exportPreview) return
    const blob = new Blob([exportPreview.csvText], { type: "text/csv;charset=utf-8;" })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement("a")
    a.href     = url
    a.download = exportPreview.filename
    a.click()
    URL.revokeObjectURL(url)
  }

  function handleDownloadXlsPreview() {
    if (!exportPreview) return
    // Lightweight CSV → HTML table conversion compatible with Excel 97–2003 (.xls)
    const lines = exportPreview.csvText.trim().split("\n")
    const parseRow = (line: string) => {
      const cells: string[] = []
      let cur = "", inQ = false
      for (let i = 0; i < line.length; i++) {
        const ch = line[i]
        if (inQ) {
          if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++ }
          else if (ch === '"') { inQ = false }
          else { cur += ch }
        } else {
          if (ch === '"') { inQ = true }
          else if (ch === ",") { cells.push(cur); cur = "" }
          else { cur += ch }
        }
      }
      cells.push(cur)
      return cells
    }
    const headers = parseRow(lines[0] ?? "")
    const rows    = lines.slice(1).map(parseRow)
    const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    let html = `<!DOCTYPE html><html xmlns:o=\"urn:schemas-microsoft-com:office:office\" xmlns:x=\"urn:schemas-microsoft-com:office:excel\" xmlns=\"http://www.w3.org/TR/REC-html40\"><head><meta charset=\"utf-8\"></head><body><table>`
    html += "<thead><tr>" + headers.map(h => `<th>${esc(h)}</th>`).join("") + "</tr></thead>"
    html += "<tbody>" + rows.map(r => `<tr>${r.map(c => `<td>${esc(c)}</td>`).join("")}</tr>`).join("") + "</tbody>"
    html += "</table></body></html>"
    const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement("a")
    a.href     = url
    a.download = exportPreview.filename.replace(/\.csv$/i, ".xls")
    a.click()
    URL.revokeObjectURL(url)
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <SiteHeader title="SmartGT — CRTs" />
      <div className="flex flex-col gap-6 p-4 md:p-6">

        {/* ── Upload card ─────────────────────────────────────────────── */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Importar archivo SmartGT</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">

            {/* ── Trigger row */}
            <div className="flex items-center gap-3 flex-wrap">
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt,.csv,.xls,.xlsx"
                className="hidden"
                onChange={handleFileChange}
              />
              <Select value={formatVersion} onValueChange={(v) => setFormatVersion(v as "v1" | "v2")}>
                <SelectTrigger className="w-32 h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="v2">Formato v2</SelectItem>
                  <SelectItem value="v1">Formato v1</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={importMut.isPending || validateHeadersMut.isPending}
              >
                <UploadIcon className="size-4 mr-2" />
                {validateHeadersMut.isPending ? "Verificando..." : importMut.isPending ? "Importando..." : "Seleccionar archivo"}
              </Button>
              <Button
                variant="outline"
                onClick={() => recalcMut.mutate()}
                disabled={recalcMut.isPending}
                title="Recalcula los contadores de problemas para todos los CRTs"
              >
                <RefreshCwIcon className={`size-4 mr-2 ${recalcMut.isPending ? "animate-spin" : ""}`} />
                {recalcMut.isPending ? "Recalculando..." : "Recalcular alertas"}
              </Button>
              <a
                href={formatVersion === "v2" ? "/smartGT-ejemplo-v2.xlsx" : "/smartGT-ejemplo.csv"}
                download={formatVersion === "v2" ? "smartGT-ejemplo-v2.xlsx" : "smartGT-ejemplo.csv"}
                className="inline-flex items-center gap-2 h-8 rounded-md px-3 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                <DownloadIcon className="size-4" />
                Descargar ejemplo {formatVersion.toUpperCase()}
              </a>
              {importMut.isPending && pendingFile && (
                <span className="text-sm text-muted-foreground truncate max-w-xs">
                  {pendingFile}
                </span>
              )}

            </div>

            {/* ── Progress bar (visible while importing) */}
            {importMut.isPending && (
              <div className="flex flex-col gap-1.5">
                <Progress className="h-1.5" />
                <p className="text-xs text-muted-foreground animate-pulse">
                  Procesando archivo, aguardá un momento…
                </p>
              </div>
            )}

            {/* ── Error de subida (HTTP / validación) */}
            {uploadError && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2.5 text-sm text-destructive">
                <span className="mt-0.5 shrink-0">⚠️</span>
                <span>{uploadError}</span>
              </div>
            )}

            {/* ── Resultado de la importación */}
            {importMut.isSuccess && importMut.data && (() => {
              const d = importMut.data
              const total = d.imported + d.updated + d.unchanged + (d.rowErrors?.length ?? 0)
              const hasActivity = d.imported > 0 || d.updated > 0
              return (
                <div className="flex flex-col gap-3">
                  {/* Summary row */}
                  <div className="flex flex-wrap items-center gap-2">
                    {d.imported > 0 && (
                      <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                        ✓ {d.imported} nuevo{d.imported !== 1 ? "s" : ""}
                      </Badge>
                    )}
                    {d.updated > 0 && (
                      <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
                        ↻ {d.updated} actualizado{d.updated !== 1 ? "s" : ""}
                      </Badge>
                    )}
                    {d.unchanged > 0 && (
                      <Badge variant="outline" className="text-muted-foreground">
                        {d.unchanged} sin cambios
                      </Badge>
                    )}
                    {(d.rowErrors?.length ?? 0) > 0 && (
                      <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
                        ✕ {d.rowErrors.length} error{d.rowErrors.length !== 1 ? "es" : ""}
                      </Badge>
                    )}
                    {d.warnings.length > 0 && (
                      <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
                        ⚠ {d.warnings.length} advertencia{d.warnings.length !== 1 ? "s" : ""}
                      </Badge>
                    )}
                  </div>

                  {/* Context message */}
                  <p className="text-xs text-muted-foreground">
                    {pendingFile && <span className="font-medium">{pendingFile}</span>}
                    {" — "}{total} fila{total !== 1 ? "s" : ""} procesada{total !== 1 ? "s" : ""}
                    {hasActivity
                      ? ". Lista actualizada."
                      : ". No hubo cambios en la base de datos."}
                  </p>

                  {/* Collapsible row errors */}
                  {(d.rowErrors?.length ?? 0) > 0 && (
                    <div>
                      <button
                        type="button"
                        onClick={() => setErrorsOpen((v) => !v)}
                        className="flex items-center gap-1.5 text-sm font-medium text-destructive hover:underline"
                      >
                        {errorsOpen ? <ChevronDownIcon className="size-4" /> : <ChevronRightIcon className="size-4" />}
                        Ver errores por fila ({d.rowErrors.length})
                      </button>
                      {errorsOpen && (
                        <ul className="mt-2 border rounded-lg overflow-hidden">
                          {d.rowErrors.map((e, i) => (
                            <li key={i} className="flex flex-wrap items-start gap-2 px-3 py-2 text-xs border-b last:border-0 bg-destructive/5">
                              <span className="text-muted-foreground shrink-0">Línea {e.line}</span>
                              <span className="font-mono bg-muted px-1.5 py-0.5 rounded shrink-0">{e.crt}</span>
                              <span className="text-muted-foreground shrink-0">ID {e.sourceId}</span>
                              <span className="text-destructive">{e.error}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  {/* Collapsible header diff */}
                  {d.headerDiff && !d.headerDiff.ok && (
                    <div>
                      <button
                        type="button"
                        onClick={() => setHeaderDiffOpen((v) => !v)}
                        className="flex items-center gap-1.5 text-sm font-medium text-amber-700 hover:underline"
                      >
                        {headerDiffOpen ? <ChevronDownIcon className="size-4" /> : <ChevronRightIcon className="size-4" />}
                        Cabecera con diferencias — {d.headerDiff.mismatches.length} columna{d.headerDiff.mismatches.length !== 1 ? "s" : ""} distinta{d.headerDiff.mismatches.length !== 1 ? "s" : ""}
                        <span className="text-xs font-normal text-muted-foreground">
                          (esperadas {d.headerDiff.expectedCount}, encontradas {d.headerDiff.foundCount})
                        </span>
                      </button>
                      {headerDiffOpen && (
                        <div className="mt-2 border rounded-lg overflow-hidden">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="bg-amber-50 dark:bg-amber-900/20 border-b">
                                <th className="text-left px-3 py-2 font-medium text-muted-foreground w-12">#</th>
                                <th className="text-left px-3 py-2 font-medium text-muted-foreground">Esperado</th>
                                <th className="text-left px-3 py-2 font-medium text-muted-foreground">Encontrado</th>
                              </tr>
                            </thead>
                            <tbody>
                              {d.headerDiff.mismatches.map((m) => (
                                <tr key={m.index} className="border-b last:border-0 bg-amber-50/50 dark:bg-amber-900/10">
                                  <td className="px-3 py-1.5 text-muted-foreground font-mono">{m.index}</td>
                                  <td className="px-3 py-1.5">
                                    {m.expected
                                      ? <span className="font-medium">{m.expected}</span>
                                      : <span className="italic text-muted-foreground">(vacío)</span>
                                    }
                                  </td>
                                  <td className="px-3 py-1.5">
                                    {m.found
                                      ? <span className="text-amber-700 dark:text-amber-400">{m.found}</span>
                                      : <span className="italic text-muted-foreground">(vacío / faltante)</span>
                                    }
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Collapsible warnings */}
                  {d.warnings.length > 0 && (
                    <div>
                      <button
                        type="button"
                        onClick={() => setWarningsOpen((v) => !v)}
                        className="flex items-center gap-1.5 text-sm font-medium text-yellow-700 hover:underline"
                      >
                        {warningsOpen ? <ChevronDownIcon className="size-4" /> : <ChevronRightIcon className="size-4" />}
                        Ver advertencias ({d.warnings.length})
                      </button>
                      {warningsOpen && (
                        <ul className="mt-2 border rounded-lg overflow-hidden">
                          {d.warnings.map((w, i) => (
                            <li key={i} className="flex flex-wrap items-center gap-2 px-3 py-2 text-xs border-b last:border-0 bg-yellow-50 dark:bg-yellow-900/10">
                              <span className="font-mono bg-muted px-1.5 py-0.5 rounded shrink-0">{w.crt}</span>
                              <span className="text-muted-foreground shrink-0">ID {w.sourceId}</span>
                              <span className="text-muted-foreground">Campos: {w.fields.join(", ")}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )
            })()}

          </CardContent>
        </Card>

        {/* ── Filter bar ──────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-3">
          {/* CRT search */}
          <div className="relative flex-1 min-w-40">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              placeholder="Buscar CRT..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>

          {/* Client search */}
          <div className="relative flex-1 min-w-44">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              placeholder="Buscar cliente..."
              value={client}
              onChange={(e) => setClient(e.target.value)}
              className="pl-8"
            />
          </div>

          {/* Driver search */}
          <div className="relative flex-1 min-w-44">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              placeholder="Buscar chofer..."
              value={driver}
              onChange={(e) => setDriver(e.target.value)}
              className="pl-8"
            />
          </div>

          <FilterCombobox
            value={origin}
            onChange={setOrigin}
            options={filterOptions?.origins ?? []}
            placeholder="Origen..."
            className="flex-1 min-w-44"
          />
          <FilterCombobox
            value={destination}
            onChange={setDestination}
            options={filterOptions?.destinations ?? []}
            placeholder="Destino..."
            className="flex-1 min-w-44"
          />
          <Select value={crtStatusFilter || "__all__"} onValueChange={(v) => setCrtStatusFilter(v === "__all__" ? "" : (v ?? ""))}>
            <SelectTrigger className="w-40" size="sm">
              <SelectValue>
                {crtStatusFilter || "Todos los estados"}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos los estados</SelectItem>
              <SelectItem value="Abierto">Abierto</SelectItem>
              <SelectItem value="Confirmado">Confirmado</SelectItem>
              <SelectItem value="En valorización">En valorización</SelectItem>
              <SelectItem value="Valorizado">Valorizado</SelectItem>
              <SelectItem value="Excluidos">Excluidos</SelectItem>
            </SelectContent>
          </Select>

          {/* Lleg. coleta date range */}
          <div className="flex items-center gap-1.5 shrink-0">
            <CalendarIcon className="size-3.5 text-muted-foreground shrink-0" />
            <input
              type="date"
              value={collectionArrivalFrom}
              onChange={(e) => setCollectionArrivalFrom(e.target.value)}
              title="Lleg. coleta — desde"
              className="h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground [color-scheme:light] dark:[color-scheme:dark] focus:outline-none focus:ring-1 focus:ring-ring w-32"
            />
            <span className="text-xs text-muted-foreground">–</span>
            <input
              type="date"
              value={collectionArrivalTo}
              onChange={(e) => setCollectionArrivalTo(e.target.value)}
              title="Lleg. coleta — hasta"
              className="h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground [color-scheme:light] dark:[color-scheme:dark] focus:outline-none focus:ring-1 focus:ring-ring w-32"
            />
            {(collectionArrivalFrom || collectionArrivalTo) && (
              <button
                type="button"
                onClick={() => { setCollectionArrivalFrom(""); setCollectionArrivalTo("") }}
                className="text-muted-foreground hover:text-foreground transition-colors"
                title="Limpiar fechas"
              >
                <XIcon className="size-3.5" />
              </button>
            )}
          </div>

          {/* Page size selector + export */}
          <div className="flex items-center gap-2 shrink-0 ml-auto">
            <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={handleExportFullList} title="Exporta todos los CRTs con sus detalles aplicando los filtros activos">
              <DownloadIcon className="size-3.5" /> Exportar
            </Button>
            <div className="flex items-center gap-1">
              {PAGE_SIZE_OPTIONS.map((size) => (
                <button
                  key={size}
                  onClick={() => { setPageSize(size); setPage(1) }}
                  className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                    pageSize === size
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted text-muted-foreground"
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Export rules panel ────────────────────────────────── */}
        <div className="rounded-lg border overflow-hidden">
          <button
            type="button"
            onClick={() => setRulesOpen((v) => !v)}
            className="flex w-full items-center justify-between px-4 py-2.5 text-sm hover:bg-muted/40 transition-colors"
          >
            <span className="flex items-center gap-2 font-medium">
              <SlidersHorizontalIcon className="size-4 text-muted-foreground" />
              Reglas de exportación
            </span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">
                {[exportRules.tripData.mergeByDriver && "Viaje único", exportRules.tripData.replaceTJClient && "3a TJ", exportRules.tripData.applyFleteroRules && "3b Fleteros", exportRules.cargoData.groupByCrt && "1 fila/CRT"].filter(Boolean).join(" · ")}
              </span>
              <ChevronDownIcon className={cn("size-4 text-muted-foreground transition-transform", rulesOpen && "rotate-180")} />
            </div>
          </button>

          {rulesOpen && (
            <div className="border-t px-4 py-4 space-y-4 bg-muted/20">
              <p className="text-xs text-muted-foreground">
                Configuración de cómo se generan las filas en cada tipo de exportación.
              </p>

              {/* Regla 1: viaje único — fusionar CRTs por chofer/cliente/fecha */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="rule-merge-by-driver"
                  checked={exportRules.tripData.mergeByDriver}
                  onCheckedChange={(v) =>
                    setExportRules((r) => ({ ...r, tripData: { ...r.tripData, mergeByDriver: !!v } }))
                  }
                />
                <div className="grid gap-0.5 -mt-0.5">
                  <label htmlFor="rule-merge-by-driver" className="text-sm font-medium cursor-pointer leading-none">
                    <span className="text-muted-foreground font-normal mr-1">1.</span>Viaje único por chofer — merge de viaje y carga
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Fusiona CRTs con el mismo chofer, mismo cliente y llegada a colecta con diferencia ≤ {exportRules.tripData.mergeMaxDaysDiff} días. Mondelez (legajo 175): 1 viaje → N cargas. Otros clientes: 1 viaje → 1 carga (BULTOS=1). Si el chofer ya tiene un CRT confirmado en esa ventana, se omite la fila de viaje.
                  </p>
                  {exportRules.tripData.mergeByDriver && (
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-xs text-muted-foreground">Máx. días diferencia:</span>
                      <input
                        type="number" min={0} max={30}
                        value={exportRules.tripData.mergeMaxDaysDiff}
                        onChange={(e) => setExportRules((r) => ({ ...r, tripData: { ...r.tripData, mergeMaxDaysDiff: Math.max(0, Math.min(30, Number(e.target.value))) } }))}
                        className="w-14 h-6 rounded border border-input bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Regla 2: agrupar detalles por CRT en datos de carga */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="rule-group-by-crt"
                  checked={exportRules.cargoData.groupByCrt}
                  onCheckedChange={(v) =>
                    setExportRules((r) => ({ ...r, cargoData: { ...r.cargoData, groupByCrt: !!v } }))
                  }
                />
                <div className="grid gap-0.5 -mt-0.5">
                  <label htmlFor="rule-group-by-crt" className="text-sm font-medium cursor-pointer leading-none">
                    <span className="text-muted-foreground font-normal mr-1">2.</span>Datos de carga — 1 fila por CRT
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Un CRT con múltiples choferes genera una sola fila. Desactivar si cada detalle es un envío independiente.
                  </p>
                </div>
              </div>

              {/* Regla 3a: TransJoven carrier + propietario propio → reemplazar cliente */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="rule-replace-tj-client"
                  checked={exportRules.tripData.replaceTJClient}
                  onCheckedChange={(v) =>
                    setExportRules((r) => ({ ...r, tripData: { ...r.tripData, replaceTJClient: !!v } }))
                  }
                />
                <div className="grid gap-0.5 -mt-0.5">
                  <label htmlFor="rule-replace-tj-client" className="text-sm font-medium cursor-pointer leading-none">
                    <span className="text-muted-foreground font-normal mr-1">3a.</span>Viajes TransJoven — reemplazar cliente{" "}
                    <span className="text-xs font-normal text-muted-foreground">(TransJoven + propietario propio)</span>
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Cuando la transportadora es TransJoven y el propietario del camión es CDC o Kaizen,
                    reemplaza el cliente original por{" "}
                    <span className="font-medium">TransJoven</span> en datos de carga y documentos.
                    Estos CRTs muestran el badge azul <span className="inline-flex items-center rounded px-1 text-[10px] font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">TJ</span> en la tabla.{" "}
                    Excepción <span className="font-medium">3a(bis)</span>: si el propietario también es TransJovem (camión propio de TJ), no se genera carga ni documentos de carga.
                  </p>
                </div>
              </div>

              {/* Regla 3b: Fletero (owner externo) + TJ/CDC carrier */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="rule-fletero"
                  checked={exportRules.tripData.applyFleteroRules}
                  onCheckedChange={(v) =>
                    setExportRules((r) => ({ ...r, tripData: { ...r.tripData, applyFleteroRules: !!v } }))
                  }
                />
                <div className="grid gap-0.5 -mt-0.5">
                  <label htmlFor="rule-fletero" className="text-sm font-medium cursor-pointer leading-none">
                    <span className="text-muted-foreground font-normal mr-1">3b.</span>Viajes fleteros — propietario externo{" "}
                    <span className="text-xs font-normal text-muted-foreground">(CDC o TransJoven + fletero)</span>
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Cuando el propietario del camión es un tercero ajeno a CDC, Kaizen y TransJoven:{" "}
                    con carrier <span className="font-medium">CDC</span>, el viaje va a{" "}
                    <span className="font-mono text-[11px]">FLETEROS VARIOS</span> sin patentes (carga y docs sin cambios);{" "}
                    con carrier <span className="font-medium">TransJovem</span>, el CRT se{" "}
                    <span className="font-medium">excluye de todos los exports</span>.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Selection action bar ────────────────────────────── */}
        {selectedIds.size > 0 && (
          <div className="sticky top-0 z-20 flex flex-wrap items-center gap-2 rounded-lg border bg-background/95 backdrop-blur-sm px-4 py-2.5 shadow-sm">
            <span className="text-sm font-medium text-muted-foreground shrink-0">
              {selectedIds.size} CRT{selectedIds.size !== 1 ? "s" : ""} seleccionado{selectedIds.size !== 1 ? "s" : ""}
            </span>
            <div className="flex flex-wrap items-center gap-2 flex-1">
              <Button variant="outline" size="sm" className="gap-2 h-8" onClick={() => handleExport("cargo-data")} disabled={hasWarningsSelected} title={hasWarningsSelected ? "No se puede exportar: hay CRTs con problemas de mapeo" : undefined}>
                <PackageIcon className="size-3.5" /> Exportar datos de carga
              </Button>
              <Button variant="outline" size="sm" className="gap-2 h-8" onClick={() => handleExport("trip-data")} disabled={hasWarningsSelected} title={hasWarningsSelected ? "No se puede exportar: hay CRTs con problemas de mapeo" : undefined}>
                <TruckIcon className="size-3.5" /> Exportar datos de viaje
              </Button>
              <Button variant="outline" size="sm" className="gap-2 h-8" onClick={() => handleExport("cargo-documents")} disabled={hasWarningsSelected} title={hasWarningsSelected ? "No se puede exportar: hay CRTs con problemas de mapeo" : undefined}>
                <FileTextIcon className="size-3.5" /> Exportar documentos de carga
              </Button>
              <Button variant="outline" size="sm" className="gap-2 h-8" onClick={() => handleExport("valuation")} disabled={hasWarningsSelected} title={hasWarningsSelected ? "No se puede exportar: hay CRTs con problemas de mapeo" : undefined}>
                <CalculatorIcon className="size-3.5" /> Exportar valorización
              </Button>

              {can("write", "smartgt") && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2 h-8"
                  disabled={selectedIds.size !== 2}
                  title={
                    selectedIds.size < 2  ? `Seleccioná exactamente 2 CRTs (${selectedIds.size}/2)` :
                    selectedIds.size > 2  ? "Seleccioná solo 2 CRTs" :
                    "Crear conexión entre destinos"
                  }
                  onClick={() => {
                    setConnectSwapped(false)
                    setConnectError(null)
                    setConnectOpen(true)
                  }}
                >
                  <PlusIcon className="size-3.5" />
                  Conexión entre destinos
                  <span className={`text-[10px] px-1.5 py-0 rounded-full font-semibold ${
                    selectedIds.size === 2
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground"
                  }`}>
                    {selectedIds.size}/2
                  </span>
                </Button>
              )}

              {hasWarningsSelected && (
                <div className="inline-flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400 font-medium ml-auto">
                  <AlertTriangleIcon className="size-3.5" />
                  No se puede exportar: {problemsSelectedCount} CRT{problemsSelectedCount !== 1 ? "s" : ""} con problemas de mapeo
                </div>
              )}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <Select
                onValueChange={(status) => {
                  setBulkStatusError(null)
                  setBulkStatusPending(status as string)
                }}
                disabled={bulkStatusMut.isPending}
              >
                <SelectTrigger size="sm" className="h-8 w-44">
                  <SelectValue placeholder="Cambiar estado...">
                    {bulkStatusMut.isPending ? "Actualizando..." : "Cambiar estado..."}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Abierto">Abierto</SelectItem>
                  <SelectItem value="Confirmado">Confirmado</SelectItem>
                  <SelectItem value="En valorización">En valorización</SelectItem>
                  <SelectItem value="Valorizado">Valorizado</SelectItem>
                  <SelectItem value="Excluidos">Excluidos</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="destructive"
                size="sm"
                className="h-8 px-2"
                onClick={() => { setBulkDeleteError(null); setBulkDeleteOpen(true) }}
                disabled={bulkDeleteMut.isPending}
                title={`Eliminar ${selectedIds.size} CRT${selectedIds.size !== 1 ? "s" : ""}`}
              >
                <Trash2Icon className="size-4" />
              </Button>
              {bulkStatusError && (
                <span className="text-xs text-destructive">{bulkStatusError}</span>
              )}
            </div>
            <Button
              variant="ghost" size="sm" className="h-8 px-2 shrink-0"
              onClick={() => setSelectedIds(new Set())}
              title="Limpiar selección"
            >
              <XIcon className="size-4" />
            </Button>
          </div>
        )}

        {/* ── Table ───────────────────────────────────────────────────────── */}
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10 pl-3">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onCheckedChange={toggleSelectAll}
                    aria-label="Seleccionar todos"
                  />
                </TableHead>
                <TableHead
                  className="w-8 cursor-pointer select-none hover:bg-muted/50 transition-colors"
                  onClick={() => handleSort("warning_count")}
                  title="Ordenar por problemas de mapeo"
                >
                  <span className="flex items-center gap-0.5">
                    <AlertTriangleIcon className="size-3.5 text-muted-foreground" />
                    <SortIcon col="warning_count" sortBy={sortBy} sortDir={sortDir} />
                  </span>
                </TableHead>
                <TableHead
                  className="w-28 cursor-pointer select-none hover:bg-muted/50 transition-colors"
                  onClick={() => handleSort("crt")}
                >
                  <span className="flex items-center gap-1">
                    CRT
                    <SortIcon col="crt" sortBy={sortBy} sortDir={sortDir} />
                  </span>
                </TableHead>
                <TableHead className="w-[clamp(260px,26vw,380px)]">Producto</TableHead>
                <TableHead className="w-[clamp(260px,26vw,380px)]">Remitente</TableHead>
                <TableHead className="w-[clamp(260px,26vw,380px)]">Destinatario</TableHead>
                <TableHead className="w-24 text-right">KM</TableHead>
                <TableHead className="w-[clamp(260px,26vw,380px)]">Cliente</TableHead>
                <TableHead
                  className="w-16 text-center cursor-pointer select-none hover:bg-muted/50 transition-colors"
                  onClick={() => handleSort("collection_arrival")}
                  title="Ordenar por llegada a colecta"
                >
                  <span className="flex items-center justify-center gap-0.5 text-[11px]">
                    Lleg.
                    <SortIcon col="collection_arrival" sortBy={sortBy} sortDir={sortDir} />
                  </span>
                </TableHead>
                <TableHead className="w-20">Estado</TableHead>
                <TableHead
                  className="w-20 cursor-pointer select-none hover:bg-muted/50 transition-colors"
                  onClick={() => handleSort("delivery_status")}
                  title="Ordenar por estado de viaje"
                >
                  <span className="flex items-center gap-1">
                    Viaje
                    <SortIcon col="delivery_status" sortBy={sortBy} sortDir={sortDir} />
                  </span>
                </TableHead>
                <TableHead
                  className="w-fit text-center cursor-pointer select-none hover:bg-muted/50 transition-colors"
                  onClick={() => handleSort("detail_count")}
                >
                  <span className="flex items-center justify-center gap-1">
                    Detalles
                    <SortIcon col="detail_count" sortBy={sortBy} sortDir={sortDir} />
                  </span>
                </TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={COL_COUNT}
                    className="text-center py-16 text-muted-foreground"
                  >
                    Cargando CRTs...
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={COL_COUNT}
                    className="text-center py-16 text-muted-foreground"
                  >
                    {search || client || statusConfig || origin || destination ||
                     collectionArrivalFrom || collectionArrivalTo
                      ? "Sin resultados para los filtros aplicados"
                      : "No hay CRTs importados aún"}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <React.Fragment key={row.id}>
                    {/* ── Main CRT row ── */}
                    <TableRow>
                      <TableCell className="w-10 pl-3">
                        <Checkbox
                          checked={selectedIds.has(row.id)}
                          onCheckedChange={() => toggleSelect(row.id)}
                          aria-label="Seleccionar fila"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                      <TableCell className="w-8">
                        {row.warningCount === null ? (
                          <span className="size-5 block" />
                        ) : row.warningCount === 0 ? (
                          <span
                            className="inline-flex items-center justify-center size-5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400 text-xs"
                            title="Sin problemas de mapeo"
                          >
                            ✓
                          </span>
                        ) : row.warningCount <= 2 ? (
                          <span
                            className="inline-flex items-center justify-center size-5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400 text-xs font-semibold"
                            title={`${row.warningCount} campo${row.warningCount !== 1 ? "s" : ""} sin mapeo`}
                          >
                            {row.warningCount}
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center justify-center size-5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 text-xs font-semibold"
                            title={`${row.warningCount} campos sin mapeo`}
                          >
                            {row.warningCount}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col items-start gap-0.5">
                          <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded tracking-wide">
                            {row.crt}
                          </span>
                          {row.isManual && (
                            <span className="text-[10px] px-1.5 py-px rounded bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400 font-medium" title="CRT creado manualmente">
                              manual
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="w-[clamp(260px,26vw,380px)] whitespace-normal text-sm">
                        {row.product ? (
                          row.matchedProductId ? (
                            <Link
                              to={`/cargos/products/${row.matchedProductId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex w-full min-w-0 items-start gap-1 leading-snug text-primary hover:underline"
                              title="Ver producto registrado"
                            >
                              <span className="min-w-0 line-clamp-2 wrap-break-word whitespace-normal">{row.product}</span>
                              <ExternalLinkIcon className="mt-0.5 size-3 shrink-0 opacity-60" />
                            </Link>
                          ) : (
                            <span
                              className="flex w-full min-w-0 items-start gap-1.5 leading-snug cursor-pointer group"
                              title="Producto no registrado — click para corregir"
                              onClick={() => setProductsModal({ name: row.product! })}
                            >
                              <span className="min-w-0 line-clamp-2 wrap-break-word whitespace-normal font-medium group-hover:underline">{row.product}</span>
                              <AlertTriangleIcon className="mt-0.5 size-3.5 shrink-0 text-amber-500" />
                            </span>
                          )
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="w-[clamp(260px,26vw,380px)] whitespace-normal">
                        <p className="text-sm font-medium leading-tight wrap-break-word">{row.shipperLocation ?? "—"}</p>
                        {(() => {
                          const usingFallback = !row.shipperCity && !!row.loadingCity
                          const city    = row.shipperCity ?? row.loadingCity
                          const matchId = row.shipperCity ? row.matchedShipperCityId : row.matchedLoadingCityId
                          if (!city && !row.shipperCountry) return null
                          return (
                            <p className="mt-0.5 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                              {city && (
                                matchId ? (
                                  <Link
                                    to={`/geo/cities/${matchId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-0.5 text-primary hover:underline"
                                    title={usingFallback ? "Ciudad de carga (fallback)" : "Ciudad registrada"}
                                  >
                                    {city}<ExternalLinkIcon className="size-3 shrink-0 opacity-60" />
                                  </Link>
                                ) : (
                                  <span className="inline-flex items-center gap-0.5" title={usingFallback ? "Ciudad de carga (fallback)" : "Ciudad no registrada"}>
                                    {city}{!usingFallback && <AlertTriangleIcon className="size-3 shrink-0 text-amber-500" />}
                                  </span>
                                )
                              )}
                              {usingFallback && city && (
                                <span className="text-[10px] px-1 py-px rounded bg-muted text-muted-foreground bg-red-100" title="Ciudad tomada del campo Lugar de carga">carga</span>
                              )}
                              {city && row.shipperCountry && <span>/</span>}
                              {row.shipperCountry}
                            </p>
                          )
                        })()}
                      </TableCell>
                      <TableCell className="w-[clamp(260px,26vw,380px)] whitespace-normal">
                        <p className="text-sm font-medium leading-tight wrap-break-word">{row.recipientLocation ?? "—"}</p>
                        {(() => {
                          const city    = row.recipientCity    ?? row.unloadingCity
                          const country = row.recipientCountry ?? row.unloadingCountry
                          const matchId = row.recipientCity
                            ? row.matchedRecipientCityId
                            : row.matchedUnloadingCityId
                          if (!city && !country) return null
                          return (
                            <p className="mt-0.5 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                              {city && (
                                matchId ? (
                                  <Link
                                    to={`/geo/cities/${matchId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-0.5 text-primary hover:underline"
                                    title="Ciudad registrada"
                                  >
                                    {city}<ExternalLinkIcon className="size-3 shrink-0 opacity-60" />
                                  </Link>
                                ) : (
                                  <span className="inline-flex items-center gap-0.5" title="Ciudad no registrada">
                                    {city}<AlertTriangleIcon className="size-3 shrink-0 text-amber-500" />
                                  </span>
                                )
                              )}
                              {city && country && <span>/</span>}
                              {country}
                            </p>
                          )
                        })()}
                      </TableCell>
                      <TableCell className="text-right">
                        {row.matchedRouteId ? (
                          <Link
                            to={`/geo/routes/${row.matchedRouteId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-end gap-1 text-sm font-mono text-primary hover:underline"
                            title="Ver ruta registrada"
                          >
                            {row.matchedRouteKm != null ? `${row.matchedRouteKm} km` : "—"}
                            <ExternalLinkIcon className="size-3 shrink-0 opacity-60" />
                          </Link>
                        ) : row.matchedRouteKm === null && (row.matchedShipperCityId || row.matchedRecipientCityId || row.matchedUnloadingCityId) ? (
                          <button
                            className="inline-flex items-center justify-end gap-1 text-xs text-muted-foreground hover:text-amber-600 transition-colors cursor-pointer"
                            title="Ruta no registrada — click para crear"
                            onClick={() => {
                              setQuickRouteTarget({
                                originCityId:   row.matchedShipperCityId ?? row.matchedLoadingCityId ?? "",
                                targetCityId:   row.matchedRecipientCityId ?? row.matchedUnloadingCityId ?? "",
                                originCityName: row.shipperCity ?? row.loadingCity ?? "",
                                targetCityName: row.recipientCity ?? row.unloadingCity ?? "",
                              })
                            }}
                          >
                            —<AlertTriangleIcon className="size-3 shrink-0 text-amber-500" />
                          </button>
                        ) : (
                          <span className="text-muted-foreground/40 text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell className="w-[clamp(260px,26vw,380px)] whitespace-normal text-sm">
                        {row.client ? (
                          row.matchedClientId ? (
                            <span className="flex w-full min-w-0 items-start gap-1.5 leading-snug">
                              <Link
                                to={`/clients/${row.matchedClientId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex min-w-0 items-start gap-1 text-primary hover:underline"
                                title="Ver cliente registrado"
                              >
                                <span className="min-w-0 line-clamp-2 wrap-break-word whitespace-normal">{row.client}</span>
                                <ExternalLinkIcon className="mt-0.5 size-3 shrink-0 opacity-60" />
                              </Link>
                              {/* Regla 3a: badge azul TJ cuando cliente mapeado */}
                              {row.tjRule3 && (
                                <Badge
                                  className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 text-[10px] px-1.5 py-0 shrink-0"
                                  title="Regla 3a: transportadora TransJovem — cliente se reemplaza por TransJovem en exports (owner: CDC/Kaizen)"
                                >
                                  TJ
                                </Badge>
                              )}
                              {/* Regla 3a(bis): badge naranja TJ cuando owner también es TJ */}
                              {row.tjRule3bis && (
                                <Badge
                                  className="bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 text-[10px] px-1.5 py-0 shrink-0"
                                  title="Regla 3a(bis): transportadora y propietario TransJovem — excluido de datos de carga y documentos"
                                >
                                  TJ
                                </Badge>
                              )}
                            </span>
                          ) : (
                            <span
                              className="flex w-full min-w-0 items-start gap-1.5 leading-snug cursor-pointer group"
                              title="Cliente no registrado — click para corregir"
                              onClick={() => setClientsModal({ name: row.client! })}
                            >
                              <span className="min-w-0 line-clamp-2 wrap-break-word whitespace-normal group-hover:underline">{row.client}</span>
                              {/* Regla 3a: badge azul TJ, sin ícono de advertencia */}
                              {row.tjRule3 ? (
                                <Badge
                                  className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 text-[10px] px-1.5 py-0"
                                  title="Regla 3a: transportadora TransJovem — cliente se reemplaza por TransJovem en exports (owner: CDC/Kaizen)"
                                >
                                  TJ
                                </Badge>
                              ) : row.tjRule3bis ? (
                                <Badge
                                  className="bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 text-[10px] px-1.5 py-0"
                                  title="Regla 3a(bis): transportadora y propietario TransJovem — excluido de datos de carga y documentos"
                                >
                                  TJ
                                </Badge>
                              ) : (
                                <AlertTriangleIcon className="mt-0.5 size-3.5 shrink-0 text-amber-500" />
                              )}
                            </span>
                          )
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {row.collectionArrival ? (
                          <span
                            className="font-mono text-xs tabular-nums"
                            title={new Date(row.collectionArrival).toLocaleString("es-AR", { timeZone: "America/Argentina/Buenos_Aires" })}
                          >
                            {new Date(row.collectionArrival).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", timeZone: "America/Argentina/Buenos_Aires" })}
                          </span>
                        ) : <span className="text-muted-foreground text-xs">—</span>}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const s = row.crtStatus
                          const cls =
                            s === "Valorizado"      ? "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400"
                            : s === "En valorización" ? "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400"
                            : s === "Confirmado"    ? "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400"
                            : s === "Excluidos"     ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                            : "bg-muted text-muted-foreground"
                          return <Badge className={`${cls} hover:${cls} text-[10px] px-1.5 py-0`}>{s}</Badge>
                        })()}
                      </TableCell>
                      <TableCell>
                        {row.deliveryStatus === "Finalizado" ? (
                          <Badge className="bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-400 text-[10px] px-1.5 py-0">Finalizado</Badge>
                        ) : row.deliveryStatus === "En curso" ? (
                          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400 text-[10px] px-1.5 py-0">En curso</Badge>
                        ) : <span className="text-muted-foreground text-xs">—</span>}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-1">
                          <Badge variant="secondary" title="Total detalles">
                            {row.detailCount}
                          </Badge>

                        </div>
                      </TableCell>
                      <TableCell className="text-right pr-2">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => openCrtView(row.id)}
                          disabled={loadingCrtView === row.id}
                          title="Ver datos completos del CRT"
                        >
                          <EyeIcon className="size-3.5" />
                        </Button>
                        {can("write", "smartgt") && (
                          <>
                            <Button variant="ghost" size="icon-sm" onClick={() => openEditCrt(row)} title="Editar CRT">
                              <PencilIcon className="size-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon-sm" onClick={() => setDeletingCrt(row)} title="Eliminar CRT" className="text-destructive hover:text-destructive">
                              <Trash2Icon className="size-3.5" />
                            </Button>
                          </>
                        )}
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => handleExpand(row.id)}
                          disabled={loadingExpand === row.id}
                          title={
                            expandedId === row.id
                              ? "Colapsar detalles"
                              : "Ver detalles"
                          }
                        >
                          {expandedId === row.id ? (
                            <ChevronDownIcon className="size-4" />
                          ) : (
                            <ChevronRightIcon className="size-4" />
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>

                    {/* ── Expandable details sub-row ── */}
                    {expandedId === row.id && (
                      <TableRow className="bg-muted/30 hover:bg-muted/30">
                        <TableCell colSpan={COL_COUNT} className="p-0">
                          <div className="px-6 py-4">
                            {!expandedDetails ||
                            expandedDetails.length === 0 ? (
                              <p className="text-sm text-muted-foreground text-center py-4">
                                Sin detalles registrados para este CRT
                              </p>
                            ) : (
                              <div className="rounded-md border overflow-hidden">
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead className="text-xs">
                                        ID SmartGT
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Chofer
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Camión
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Carreta
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Carga
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Descarga
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Posición
                                      </TableHead>
                                      <TableHead className="text-xs">
                                        Status
                                      </TableHead>
                                      {can("write", "smartgt") && <TableHead className="text-xs w-20" />}
                                      <TableHead className="text-xs w-10" />
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {expandedDetails.map((d) => (
                                      <TableRow key={d.id}>
                                        <TableCell>
                                          <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">
                                            {d.sourceId}
                                          </span>
                                        </TableCell>
                                        <TableCell className="text-sm">
                                          {d.driverName ? (
                                            d.matchedDriverId ? (
                                              <Link
                                                to={`/drivers/${d.matchedDriverId}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1 text-primary hover:underline"
                                                title="Ver chofer registrado"
                                              >
                                                {d.driverName}<ExternalLinkIcon className="size-3 shrink-0 opacity-60" />
                                              </Link>
                                            ) : isFleteroOwner(d.truckOwner) ? (
                                              /* Fletero owner: chofer externo, no se exporta */
                                              <span className="inline-flex items-center gap-1.5">
                                                <span className="text-muted-foreground">{d.driverName}</span>
                                                <span className="text-[10px] px-1.5 py-0 rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 font-medium">Fletero</span>
                                              </span>
                                            ) : (
                                              <button
                                                type="button"
                                                className="inline-flex items-center gap-1.5 rounded px-1 -mx-1 hover:bg-amber-100 dark:hover:bg-amber-950/40 transition-colors cursor-pointer"
                                                title="Chofer no registrado — click para corregir"
                                                onClick={() => setDriversModal({ name: d.driverName! })}
                                              >
                                                {d.driverName}<AlertTriangleIcon className="size-3.5 shrink-0 text-amber-500" />
                                              </button>
                                            )
                                          ) : "—"}
                                        </TableCell>
                                        <TableCell className="text-sm font-mono">
                                          {d.truckPlate ? (
                                            d.matchedTruckId ? (
                                              <Link
                                                to={`/units/${d.matchedTruckId}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1 text-primary hover:underline font-mono"
                                                title="Ver camión registrado"
                                              >
                                                {d.truckPlate}<ExternalLinkIcon className="size-3 shrink-0 opacity-60" />
                                              </Link>
                                            ) : isFleteroOwner(d.truckOwner) ? (
                                              /* Fletero: placa externa, sin warning */
                                              <span className="text-muted-foreground">{d.truckPlate}</span>
                                            ) : (
                                              <button
                                                type="button"
                                                className="inline-flex items-center gap-1.5 rounded px-1 -mx-1 hover:bg-amber-100 dark:hover:bg-amber-950/40 transition-colors cursor-pointer font-mono"
                                                title="Camión no registrado — click para corregir"
                                                onClick={() => setUnitsModal({ plate: d.truckPlate!, type: "truck" })}
                                              >
                                                {d.truckPlate}<AlertTriangleIcon className="size-3.5 shrink-0 text-amber-500" />
                                              </button>
                                            )
                                          ) : "—"}
                                        </TableCell>
                                        <TableCell className="text-sm font-mono">
                                          {d.trailerPlate ? (
                                            d.matchedTrailerId ? (
                                              <Link
                                                to={`/units/${d.matchedTrailerId}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1 text-primary hover:underline font-mono"
                                                title="Ver carreta registrada"
                                              >
                                                {d.trailerPlate}<ExternalLinkIcon className="size-3 shrink-0 opacity-60" />
                                              </Link>
                                            ) : isFleteroOwner(d.truckOwner) ? (
                                              /* Fletero: carreta externa, sin warning */
                                              <span className="text-muted-foreground">{d.trailerPlate}</span>
                                            ) : (
                                              <button
                                                type="button"
                                                className="inline-flex items-center gap-1.5 rounded px-1 -mx-1 hover:bg-amber-100 dark:hover:bg-amber-950/40 transition-colors cursor-pointer font-mono"
                                                title="Carreta no registrada — click para corregir"
                                                onClick={() => setUnitsModal({ plate: d.trailerPlate!, type: "trailer" })}
                                              >
                                                {d.trailerPlate}<AlertTriangleIcon className="size-3.5 shrink-0 text-amber-500" />
                                              </button>
                                            )
                                          ) : "—"}
                                        </TableCell>
                                        <TableCell className="text-sm">
                                          {fmtDate(d.loadingDate)}
                                        </TableCell>
                                        <TableCell className="text-sm">
                                          {fmtDate(d.unloadingDate)}
                                        </TableCell>
                                        <TableCell className="text-sm">
                                          {d.cargoPosition ?? "—"}
                                        </TableCell>
                                        <TableCell className="text-sm">
                                          {d.currentStatus ?? "—"}
                                        </TableCell>

                                        <TableCell className="text-right pr-2">
                                          <Button
                                            variant="ghost"
                                            size="icon-sm"
                                            onClick={() => setDetailModal(d)}
                                            title="Ver detalle completo"
                                          >
                                            <EyeIcon className="size-3.5" />
                                          </Button>
                                        </TableCell>
                                        {can("write", "smartgt") && (
                                          <TableCell className="text-right pr-2">
                                            <Button variant="ghost" size="icon-sm" onClick={() => openEditDetail(d)} title="Editar">
                                              <PencilIcon className="size-3.5" />
                                            </Button>
                                            <Button variant="ghost" size="icon-sm" onClick={() => setDeletingDetail({ id: d.id, sourceId: d.sourceId, crtId: expandedId! })} title="Eliminar" className="text-destructive hover:text-destructive">
                                              <Trash2Icon className="size-3.5" />
                                            </Button>
                                          </TableCell>
                                        )}
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>

          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={total}
            pageSize={pageSize}
            onPageChange={setPage}
          />
        </div>
      </div>

      {/* ── Detail modal ────────────────────────────────────────────── */}
      <Dialog open={!!detailModal} onOpenChange={(o) => !o && setDetailModal(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Detalle SmartGT
              {detailModal && (
                <span className="ml-2 font-mono text-sm font-normal bg-muted px-2 py-0.5 rounded">
                  ID {detailModal.sourceId}
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              Información completa del movimiento
            </DialogDescription>
          </DialogHeader>

          {detailModal && (
            <div className="flex flex-col gap-5 text-sm">

              {/* Vehículo y chofer */}
              <section>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Vehículo y chofer</h4>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                  <Field label="Chofer"        value={detailModal.driverName} />
                  <Field label="Teléfono"       value={detailModal.driverPhone} />
                  <Field label="Camión"         value={detailModal.truckPlate} mono />
                  <Field label="Propietario"   value={detailModal.truckOwner} />
                  <Field label="Carreta"        value={detailModal.trailerPlate} mono />
                  <Field label="Peso"           value={detailModal.weight} />
                  <Field label="Adelanto chofer" value={detailModal.driverAdvanceTotal} />
                  <Field label="Fórmula"        value={detailModal.formula} />
                </dl>
              </section>

              {/* Fechas del viaje */}
              <section>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Fechas del viaje</h4>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                  <Field label="Fecha prevista"      value={fmtDate(detailModal.expectedDate)} />
                  <Field label="Fecha carga"         value={fmtDate(detailModal.loadingDate)} />
                  <Field label="Fecha descarga"      value={fmtDate(detailModal.unloadingDate)} />
                  <Field label="Días almacenado"     value={detailModal.storageDays != null ? String(detailModal.storageDays) : null} />
                  <Field label="Agendamiento coleta" value={fmtDate(detailModal.collectionScheduleDate)} />
                </dl>
              </section>

              {/* Posicionamiento */}
              <section>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Posicionamiento</h4>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                  <Field label="Posición carga"  value={detailModal.cargoPosition} />
                  <Field label="Tipo posición"   value={detailModal.positionType} />
                  <Field label="Status actual"   value={detailModal.currentStatus} />
                </dl>
              </section>

              {/* Tracking coleta y frontera */}
              <section>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Coleta y frontera</h4>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                  <Field label="Llegada a coleta"       value={fmtDatetime(detailModal.collectionArrival ?? detailModal.collectionDeparture)} />
                  <Field label="Salida de coleta"       value={fmtDatetime(detailModal.collectionDeparture)} />
                  <Field label="Prev. llegada frontera" value={fmtDatetime(detailModal.expectedArrivalBorder)} />
                  <Field label="Anticipación de clave" value={fmtDatetime(detailModal.passwordAnticipation)} />
                  <Field label="Llegada a frontera"     value={fmtDatetime(detailModal.borderArrival)} />
                  <Field label="Cruce autorizado"       value={fmtDatetime(detailModal.authorizedCrossing)} />
                  <Field label="Solicitud de clave"     value={fmtDatetime(detailModal.passwordRequest)} />
                </dl>
              </section>

              {/* Aduana origen */}
              <section>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Aduana origen exp.</h4>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                  <Field label="Entrada" value={fmtDatetime(detailModal.customsOriginEntry)} />
                  <Field label="Salida"  value={fmtDatetime(detailModal.customsOriginExit)} />
                </dl>
              </section>

              {/* Aduana tánsito */}
              <section>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Aduana tránsito</h4>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                  <Field label="Entrada" value={fmtDatetime(detailModal.customsTransitEntry)} />
                  <Field label="Salida"  value={fmtDatetime(detailModal.customsTransitExit)} />
                </dl>
              </section>

              {/* Aduana destino */}
              <section>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Aduana destino exp.</h4>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                  <Field label="Prev. llegada" value={fmtDatetime(detailModal.expectedCustomsDestination)} />
                  <Field label="Entrada"       value={fmtDatetime(detailModal.customsDestinationEntry)} />
                  <Field label="Salida"        value={fmtDatetime(detailModal.customsDestinationExit)} />
                </dl>
              </section>

              {/* Descarga */}
              <section>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Descarga</h4>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                  <Field label="Prev. llegada descarga" value={fmtDatetime(detailModal.expectedUnloadingArrival)} />
                  <Field label="Llegada a descarga"     value={fmtDatetime(detailModal.unloadingArrival)} />
                  <Field label="Salida de descarga"     value={fmtDatetime(detailModal.unloadingDeparture)} />
                </dl>
              </section>



            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── CRT view modal ────────────────────────────────────────────── */}
      <Dialog open={!!crtViewModal} onOpenChange={(o) => !o && setCrtViewModal(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Datos del CRT
              {crtViewModal && (
                <span className="ml-2 font-mono text-sm font-normal bg-muted px-2 py-0.5 rounded">
                  {crtViewModal.crt}
                </span>
              )}
            </DialogTitle>
            <DialogDescription>Información completa del documento de transporte</DialogDescription>
          </DialogHeader>

          {crtViewModal && (
            <div className="flex flex-col gap-5 text-sm">

              {/* Notas internas — destacado al tope si existe */}
              {crtViewModal.notes && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/40 px-4 py-3 flex gap-3 items-start">
                  <span className="mt-0.5 text-amber-500 dark:text-amber-400 flex-shrink-0">📝</span>
                  <div className="space-y-0.5">
                    <p className="text-xs font-semibold text-amber-800 dark:text-amber-300">Notas internas</p>
                    <p className="text-xs text-amber-900 dark:text-amber-200 whitespace-pre-wrap">{crtViewModal.notes}</p>
                  </div>
                </div>
              )}

              {/* Identificación */}
              <section>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Identificación</h4>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                  <Field label="CRT"            value={crtViewModal.crt} mono />
                  <Field label="Frontera"       value={crtViewModal.borderCrossing} />
                  <Field label="Estado config"  value={crtViewModal.statusConfig} />
                  <Field label="Tipo consulta"  value={crtViewModal.queryType} />
                  <Field label="Protocolo endoso" value={crtViewModal.endorsementProtocol} mono />
                </dl>
              </section>

              {/* Producto y carga */}
              <section>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Producto y carga</h4>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                  <Field label="Producto"          value={crtViewModal.product} />
                  <Field label="Transportadora"    value={crtViewModal.carrier} />
                  <Field label="Flete"             value={crtViewModal.freight} />
                  <Field label="Moneda"            value={crtViewModal.currency} />
                  <Field label="Valor FOB"         value={crtViewModal.fobValue} />
                  <Field label="Precinto"          value={crtViewModal.seal} mono />
                  <Field label="Contrato flete"    value={crtViewModal.freightContract} mono />
                  <Field label="Flota (etiqueta)"  value={crtViewModal.fleetLabel} />
                </dl>
              </section>

              {/* Remitente */}
              <section>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Remitente</h4>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                  <Field label="Nombre"     value={crtViewModal.shipperLocation} />
                  <Field label="Ciudad"     value={crtViewModal.shipperCity} />
                  <Field label="País"       value={crtViewModal.shipperCountry} />
                  <Field label="Lugar carga"        value={crtViewModal.loadingLocation} />
                  <Field label="Ciudad carga"       value={crtViewModal.loadingCity} />
                  <Field label="País carga"         value={crtViewModal.loadingCountry} />
                </dl>
              </section>

              {/* Destinatario */}
              <section>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Destinatario</h4>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                  <Field label="Nombre"     value={crtViewModal.recipientLocation} />
                  <Field label="Ciudad"     value={crtViewModal.recipientCity} />
                  <Field label="País"       value={crtViewModal.recipientCountry} />
                  <Field label="Lugar descarga"     value={crtViewModal.unloadingLocation} />
                  <Field label="Ciudad descarga"    value={crtViewModal.unloadingCity} />
                  <Field label="País descarga"      value={crtViewModal.unloadingCountry} />
                </dl>
              </section>

              {/* Cliente */}
              <section>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Cliente</h4>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                  <Field label="Cliente"            value={crtViewModal.client} />
                  <Field label="Nombre simplificado" value={crtViewModal.clientSimpleName} />
                </dl>
              </section>

              {/* Documentos */}
              <section>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Documentos</h4>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                  <Field label="Referencia"        value={crtViewModal.reference} mono />
                  <Field label="Factura comercial" value={crtViewModal.commercialInvoice} mono />
                  <Field label="DANFE"             value={crtViewModal.danfe} mono />
                  <Field label="Nota DANFE"        value={crtViewModal.noteDanfe} />
                  <Field label="Aduana origen"     value={crtViewModal.customsOrigin} />
                  <Field label="Aduana destino"    value={crtViewModal.customsDestination} />
                </dl>
              </section>

              {/* Observaciones SmartGT */}
              {crtViewModal.cargoControlObs && (
                <section>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Observaciones SmartGT</h4>
                  <p className="text-sm bg-muted rounded px-3 py-2 whitespace-pre-wrap">{crtViewModal.cargoControlObs}</p>
                </section>
              )}

              {/* Registro */}
              <section>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Registro</h4>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                  <Field label="Creado"       value={fmtDate(crtViewModal.createdAt)} />
                  <Field label="Actualizado"  value={fmtDate(crtViewModal.updatedAt)} />
                </dl>
              </section>

            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Header validation dialog ──────────────────────────────────── */}
      <Dialog
        open={!!headerValidation}
        onOpenChange={(o) => { if (!o) { setHeaderValidation(null); setPendingImportFile(null) } }}
      >
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {headerValidation?.headerDiff?.ok === false ? (
                <AlertTriangleIcon className="size-5 text-amber-500" />
              ) : (
                <span className="inline-flex items-center justify-center size-5 rounded-full bg-green-100 text-green-700 text-xs">✓</span>
              )}
              Validación de cabeceras — {formatVersion.toUpperCase()}
            </DialogTitle>
            <DialogDescription>
              {pendingImportFile?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-3 pr-1">
            {!headerValidation?.headerDiff ? (
              <p className="text-sm text-muted-foreground">El archivo no tiene cabecera analizable (formato TSV).</p>
            ) : headerValidation.headerDiff.ok ? (
              <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400 font-medium">
                <span className="inline-flex items-center justify-center size-5 rounded-full bg-green-100 dark:bg-green-900/40 text-xs">✓</span>
                Todas las {headerValidation.headerDiff.expectedCount} columnas coinciden con el formato {formatVersion.toUpperCase()}.
              </div>
            ) : (
              <>
                <p className="text-sm text-amber-700 dark:text-amber-400 font-medium">
                  {headerValidation.headerDiff.mismatches.length} columna{headerValidation.headerDiff.mismatches.length !== 1 ? "s" : ""} distinta{headerValidation.headerDiff.mismatches.length !== 1 ? "s" : ""}
                  {" "}— esperadas {headerValidation.headerDiff.expectedCount}, encontradas {headerValidation.headerDiff.foundCount}.
                </p>
                <div className="rounded-lg border overflow-hidden text-xs">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium text-muted-foreground w-12">#</th>
                        <th className="text-left px-3 py-2 font-medium text-muted-foreground">Esperado</th>
                        <th className="text-left px-3 py-2 font-medium text-muted-foreground">Encontrado en el archivo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {headerValidation.headerDiff.mismatches.map((m) => (
                        <tr key={m.index} className="border-t">
                          <td className="px-3 py-1.5 font-mono text-muted-foreground">{m.index}</td>
                          <td className="px-3 py-1.5">
                            {m.expected
                              ? <span className="font-medium">{m.expected}</span>
                              : <span className="italic text-muted-foreground">(fin de cabecera)</span>}
                          </td>
                          <td className="px-3 py-1.5">
                            {m.found
                              ? <span className="text-amber-700 dark:text-amber-400">{m.found}</span>
                              : <span className="italic text-muted-foreground">(ausente)</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-muted-foreground">
                  El import puede continuar igual — las columnas se leen por posición, no por nombre.
                  Si hay muchas diferencias, verificá que el archivo sea {formatVersion.toUpperCase()}.
                </p>
              </>
            )}
          </div>

          <DialogFooter className="pt-2">
            <Button
              variant="outline"
              onClick={() => { setHeaderValidation(null); setPendingImportFile(null) }}
            >
              Cancelar
            </Button>
            {headerValidation?.headerDiff?.ok !== false && (
              <Button
                onClick={() => {
                  if (pendingImportFile) {
                    setHeaderValidation(null)
                    importMut.mutate(pendingImportFile)
                    setPendingImportFile(null)
                  }
                }}
              >
                Importar
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Bulk delete confirm dialog ───────────────────────────────── */}
      <Dialog open={bulkDeleteOpen} onOpenChange={(o) => { if (!o) { setBulkDeleteOpen(false); setBulkDeleteError(null) } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Eliminar CRTs seleccionados</DialogTitle>
            <DialogDescription>
              ¿Eliminás{" "}
              <span className="font-semibold">{selectedIds.size} CRT{selectedIds.size !== 1 ? "s" : ""}</span>{" "}
              y todos sus detalles? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          {bulkDeleteError && (
            <p className="text-sm text-destructive">{bulkDeleteError}</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setBulkDeleteOpen(false); setBulkDeleteError(null) }} disabled={bulkDeleteMut.isPending}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={bulkDeleteMut.isPending}
              onClick={() => bulkDeleteMut.mutate(Array.from(selectedIds))}
            >
              {bulkDeleteMut.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Bulk status confirm dialog ───────────────────────────────── */}
      <Dialog open={!!bulkStatusPending} onOpenChange={(o) => !o && setBulkStatusPending(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Cambiar estado</DialogTitle>
            <DialogDescription>
              ¿Cambiás el estado de{" "}
              <span className="font-semibold">{selectedIds.size} CRT{selectedIds.size !== 1 ? "s" : ""}</span>{" "}
              a <span className="font-semibold">{bulkStatusPending}</span>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose>
              <Button variant="outline" onClick={() => setBulkStatusPending(null)}>Cancelar</Button>
            </DialogClose>
            <Button
              onClick={() => {
                if (bulkStatusPending) {
                  bulkStatusMut.mutate({ ids: Array.from(selectedIds), crtStatus: bulkStatusPending })
                  setBulkStatusPending(null)
                }
              }}
            >
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete CRT dialog ───────────────────────────────────────────── */}
      <Dialog open={!!deletingCrt} onOpenChange={(o) => !o && setDeletingCrt(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Eliminar CRT</DialogTitle>
            <DialogDescription>
              ¿Eliminás el CRT <span className="font-mono font-semibold">{deletingCrt?.crt}</span> y todos sus detalles? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose>
              <Button variant="outline" disabled={deleteCrtMut.isPending} onClick={() => setDeletingCrt(null)}>Cancelar</Button>
            </DialogClose>
            <Button
              variant="destructive"
              disabled={deleteCrtMut.isPending}
              onClick={() => deletingCrt && deleteCrtMut.mutate(deletingCrt.id)}
            >
              {deleteCrtMut.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Detail dialog ────────────────────────────────────── */}
      <Dialog open={!!deletingDetail} onOpenChange={(o) => !o && setDeletingDetail(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Eliminar detalle</DialogTitle>
            <DialogDescription>
              ¿Eliminás el detalle con ID SmartGT <span className="font-mono font-semibold">{deletingDetail?.sourceId}</span>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose>
              <Button variant="outline" disabled={deleteDetailMut.isPending} onClick={() => setDeletingDetail(null)}>Cancelar</Button>
            </DialogClose>
            <Button
              variant="destructive"
              disabled={deleteDetailMut.isPending}
              onClick={() => deletingDetail && deleteDetailMut.mutate(deletingDetail.id)}
            >
              {deleteDetailMut.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Edit CRT Sheet ──────────────────────────────────────────── */}
      <Sheet open={!!editingCrt} onOpenChange={(o) => !o && setEditingCrt(null)}>
        <SheetContent side="right" className="sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              Editar CRT{" "}
              <span className="font-mono text-sm font-normal">{editingCrt?.crt}</span>
            </SheetTitle>
          </SheetHeader>
          <div className="flex flex-col gap-4 py-4 px-4">
            {crtFormError && (
              <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{crtFormError}</p>
            )}
            <div className="grid gap-1.5">
              <Label className="text-xs">Estado</Label>
              <Select
                value={crtForm.crtStatus ?? "Abierto"}
                onValueChange={(v) => setCrtForm((f) => ({ ...f, crtStatus: v ?? "Abierto" }))}
              >
                <SelectTrigger>
                  <SelectValue>
                    {crtForm.crtStatus || "Abierto"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Abierto">Abierto</SelectItem>
                  <SelectItem value="Confirmado">Confirmado</SelectItem>
                  <SelectItem value="En valorización">En valorización</SelectItem>
                  <SelectItem value="Valorizado">Valorizado</SelectItem>
                  <SelectItem value="Excluidos">Excluidos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <FormRow label="Frontera" field="borderCrossing" form={crtForm} setForm={setCrtForm} />
            <FormRow label="Producto" field="product" form={crtForm} setForm={setCrtForm} />
            <FormRow label="Transportadora" field="carrier" form={crtForm} setForm={setCrtForm} />
            <FormRow label="Flete" field="freight" form={crtForm} setForm={setCrtForm} />
            <FormRow label="Ciudad Remitente" field="shipperCity" form={crtForm} setForm={setCrtForm} />
            <FormRow label="País Remitente" field="shipperCountry" form={crtForm} setForm={setCrtForm} />
            <FormRow label="Ciudad Destinatario" field="recipientCity" form={crtForm} setForm={setCrtForm} />
            <FormRow label="País Destinatario" field="recipientCountry" form={crtForm} setForm={setCrtForm} />
            <div className="grid gap-1.5">
              <Label className="text-xs">Notas</Label>
              <textarea
                value={crtForm.notes ?? ""}
                onChange={(e) => setCrtForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Observaciones o motivo de exclusión..."
                rows={3}
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-xs shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
              />
            </div>
          </div>
          <SheetFooter>
            <Button variant="outline" onClick={() => setEditingCrt(null)} disabled={updateCrtMut.isPending}>
              Cancelar
            </Button>
            <Button
              disabled={updateCrtMut.isPending}
              onClick={() => {
                if (!editingCrt) return
                const data = Object.fromEntries(
                  Object.entries(crtForm).map(([k, v]) => [k, v.trim() || null])
                )
                updateCrtMut.mutate({ id: editingCrt.id, data })
              }}
            >
              {updateCrtMut.isPending ? "Guardando..." : "Guardar"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* ── Conexión entre destinos Dialog ───────────────────────────── */}
      {connectOpen && connectFirst && connectSecond && (
        <ConnectCrtsDialog
          open={connectOpen}
          onOpenChange={(v) => { if (!v) { setConnectOpen(false); setConnectSwapped(false); setConnectError(null) } }}
          firstCrt={connectFirst}
          secondCrt={connectSecond}
          onSwap={() => setConnectSwapped(s => !s)}
          onConfirm={() => {
            if (!connectFirst || !connectSecond) return
            connectCrtMut.mutate({ firstCrtId: connectFirst.id, secondCrtId: connectSecond.id })
          }}
          isPending={connectCrtMut.isPending}
          error={connectError}
        />
      )}

      {/* ── Create CRT Sheet ───────────────────────────────────────────────── */}
      {/* ── Units Modal (truck / trailer) ─────────────────────────────────── */}
      <UnitsModal
        open={unitsModal !== null}
        onClose={() => setUnitsModal(null)}
        defaultSearch={unitsModal?.plate ?? ""}
        alertType={unitsModal?.type}
      />

      {/* ── Products Modal ────────────────────────────────────────────────── */}
      <CargoProductsModal
        open={productsModal !== null}
        onClose={() => setProductsModal(null)}
        defaultSearch={productsModal?.name ?? ""}
      />

      {/* ── Drivers Modal ─────────────────────────────────────────────────── */}
      <DriversModal
        open={driversModal !== null}
        onClose={() => setDriversModal(null)}
        defaultSearch={driversModal?.name ?? ""}
      />

      {/* ── Quick Create Route Dialog ─────────────────────────────────────── */}
      {/* ── Quick Create Route Dialog ────────────────────────────────────────────────────── */}
      <QuickRouteDialog
        target={quickRouteTarget}
        onClose={() => setQuickRouteTarget(null)}
      />

      {/* ── Clients Modal ─────────────────────────────────────────────────── */}
      <ClientsModal
        open={clientsModal !== null}
        onClose={() => setClientsModal(null)}
        defaultSearch={clientsModal?.name ?? ""}
      />

      {/* ── Create CRT Sheet ───────────────────────────────────────────────── */}
      <Sheet open={creatingCrt} onOpenChange={(o) => { if (!o) { setCreatingCrt(false); setCreateCrtForm(EMPTY_CREATE) } }}>
        <SheetContent side="right" className="sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Nuevo CRT</SheetTitle>
          </SheetHeader>
          <div className="flex flex-col gap-4 py-4 px-4">
            {createCrtError && (
              <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{createCrtError}</p>
            )}

            {/* NRO CRT */}
            <div className="grid gap-1.5">
              <Label className="text-xs">NRO CRT <span className="text-destructive">*</span></Label>
              <Input
                value={createCrtForm.crt}
                maxLength={50}
                placeholder="Ej. AR180914681"
                onChange={(e) => setCreateCrtForm(f => ({ ...f, crt: e.target.value }))}
              />
            </div>

            {/* Estado */}
            <div className="grid gap-1.5">
              <Label className="text-xs">Estado</Label>
              <Select
                value={createCrtForm.crtStatus || "Abierto"}
                onValueChange={(v) => setCreateCrtForm(f => ({ ...f, crtStatus: v as string ?? "Abierto" }))}
              >
                <SelectTrigger>
                  <SelectValue>{createCrtForm.crtStatus || "Abierto"}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Abierto">Abierto</SelectItem>
                  <SelectItem value="Confirmado">Confirmado</SelectItem>
                  <SelectItem value="En valorización">En valorización</SelectItem>
                  <SelectItem value="Valorizado">Valorizado</SelectItem>
                  <SelectItem value="Excluidos">Excluidos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Cliente */}
            <SheetSearchSelect
              label="Cliente"
              value={createCrtForm.client}
              onChange={(v) => setCreateCrtForm(f => ({ ...f, client: v }))}
              items={clientOptions.map(c => ({ label: c.name, value: c.sgtCodes![0] ?? "" }))}
              placeholder="Seleccionar cliente..."
            />

            {/* Ciudad Remitente */}
            <SheetSearchSelect
              label="Ciudad Remitente"
              value={createCrtForm.shipperCity}
              onChange={(v) => setCreateCrtForm(f => ({ ...f, shipperCity: v }))}
              items={cityOptions.map(c => ({ label: `${c.name} (${c.stateName})`, value: c.sgtCodes![0] ?? "" }))}
              placeholder="Seleccionar ciudad..."
            />

            {/* Ciudad Destino */}
            <SheetSearchSelect
              label="Ciudad Destino"
              value={createCrtForm.recipientCity}
              onChange={(v) => setCreateCrtForm(f => ({ ...f, recipientCity: v }))}
              items={cityOptions.map(c => ({ label: `${c.name} (${c.stateName})`, value: c.sgtCodes![0] ?? "" }))}
              placeholder="Seleccionar ciudad..."
            />

            <hr className="border-border" />

            {/* Chofer */}
            <SheetSearchSelect
              label="Chofer"
              value={createCrtForm.driverName}
              onChange={(v) => setCreateCrtForm(f => ({ ...f, driverName: v }))}
              items={driverOptions.map(d => ({ label: d.name, value: d.sgtCodes![0] ?? "" }))}
              placeholder="Seleccionar chofer..."
            />

            {/* Camión */}
            <div className="grid gap-1.5">
              <Label className="text-xs">Camión</Label>
              <Input
                value={createCrtForm.truckPlate}
                placeholder="Placa del camión"
                className="font-mono uppercase"
                onChange={(e) => setCreateCrtForm(f => ({ ...f, truckPlate: e.target.value.toUpperCase() }))}
              />
            </div>

            {/* Trailer */}
            <div className="grid gap-1.5">
              <Label className="text-xs">Trailer</Label>
              <Input
                value={createCrtForm.trailerPlate}
                placeholder="Placa del trailer"
                className="font-mono uppercase"
                onChange={(e) => setCreateCrtForm(f => ({ ...f, trailerPlate: e.target.value.toUpperCase() }))}
              />
            </div>

            {/* Fecha de carga */}
            <div className="grid gap-1.5">
              <Label className="text-xs">Fecha de carga</Label>
              <Input
                type="datetime-local"
                value={createCrtForm.loadingDate}
                onChange={(e) => setCreateCrtForm(f => ({ ...f, loadingDate: e.target.value }))}
              />
            </div>
          </div>
          <SheetFooter>
            <Button
              variant="outline"
              onClick={() => { setCreatingCrt(false); setCreateCrtForm(EMPTY_CREATE) }}
              disabled={createCrtMut.isPending}
            >
              Cancelar
            </Button>
            <Button
              disabled={createCrtMut.isPending}
              onClick={() => {
                if (!createCrtForm.crt.trim()) { setCreateCrtError("El número de CRT es obligatorio"); return }
                setCreateCrtError(null)
                createCrtMut.mutate(createCrtForm)
              }}
            >
              {createCrtMut.isPending ? "Guardando..." : "Guardar"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* ── Edit Detail Sheet ─────────────────────────────────────────── */}
      <Sheet open={!!editingDetail} onOpenChange={(o) => !o && setEditingDetail(null)}>
        <SheetContent side="right" className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              Editar detalle{" "}
              <span className="font-mono text-sm font-normal">ID {editingDetail?.sourceId}</span>
            </SheetTitle>
          </SheetHeader>
          <div className="flex flex-col gap-5 py-4 px-4">
            {detailFormError && (
              <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{detailFormError}</p>
            )}



            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Vehículo y chofer</h4>
              <div className="grid grid-cols-2 gap-3">
                <FormRow label="Chofer" field="driverName" form={detailForm} setForm={setDetailForm} />
                <FormRow label="Teléfono" field="driverPhone" form={detailForm} setForm={setDetailForm} />
                <FormRow label="Camión" field="truckPlate" form={detailForm} setForm={setDetailForm} mono />
                <FormRow label="Propietario" field="truckOwner" form={detailForm} setForm={setDetailForm} />
                <FormRow label="Carreta" field="trailerPlate" form={detailForm} setForm={setDetailForm} mono />
                <FormRow label="Peso" field="weight" form={detailForm} setForm={setDetailForm} />
                <FormRow label="Adelanto chofer" field="driverAdvanceTotal" form={detailForm} setForm={setDetailForm} />
                <FormRow label="Fórmula" field="formula" form={detailForm} setForm={setDetailForm} />
              </div>
            </section>

            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Estado</h4>
              <div className="grid grid-cols-2 gap-3">
                <FormRow label="Posición carga" field="cargoPosition" form={detailForm} setForm={setDetailForm} />
                <FormRow label="Tipo posición" field="positionType" form={detailForm} setForm={setDetailForm} />
                <FormRow label="Status actual" field="currentStatus" form={detailForm} setForm={setDetailForm} />
                <FormRow label="Días almacenado" field="storageDays" form={detailForm} setForm={setDetailForm} type="number" />
              </div>
            </section>

            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Fechas del viaje</h4>
              <div className="grid grid-cols-2 gap-3">
                <FormRow label="Fecha prevista" field="expectedDate" form={detailForm} setForm={setDetailForm} type="datetime-local" />
                <FormRow label="Fecha carga" field="loadingDate" form={detailForm} setForm={setDetailForm} type="datetime-local" />
                <FormRow label="Fecha descarga" field="unloadingDate" form={detailForm} setForm={setDetailForm} type="datetime-local" />
                <FormRow label="Agend. coleta" field="collectionScheduleDate" form={detailForm} setForm={setDetailForm} type="datetime-local" />
              </div>
            </section>

            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Coleta y frontera</h4>
              <div className="grid grid-cols-2 gap-3">
                <FormRow label="Llegada coleta" field="collectionArrival" form={detailForm} setForm={setDetailForm} type="datetime-local" />
                <FormRow label="Salida coleta" field="collectionDeparture" form={detailForm} setForm={setDetailForm} type="datetime-local" />
                <FormRow label="Prev. llegada frontera" field="expectedArrivalBorder" form={detailForm} setForm={setDetailForm} type="datetime-local" />
                <FormRow label="Anticip. clave" field="passwordAnticipation" form={detailForm} setForm={setDetailForm} type="datetime-local" />
                <FormRow label="Llegada frontera" field="borderArrival" form={detailForm} setForm={setDetailForm} type="datetime-local" />
                <FormRow label="Cruce autorizado" field="authorizedCrossing" form={detailForm} setForm={setDetailForm} type="datetime-local" />
                <FormRow label="Solic. clave" field="passwordRequest" form={detailForm} setForm={setDetailForm} type="datetime-local" />
              </div>
            </section>

            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Aduana origen exp.</h4>
              <div className="grid grid-cols-2 gap-3">
                <FormRow label="Entrada" field="customsOriginEntry" form={detailForm} setForm={setDetailForm} type="datetime-local" />
                <FormRow label="Salida" field="customsOriginExit" form={detailForm} setForm={setDetailForm} type="datetime-local" />
              </div>
            </section>

            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Aduana tránsito</h4>
              <div className="grid grid-cols-2 gap-3">
                <FormRow label="Entrada" field="customsTransitEntry" form={detailForm} setForm={setDetailForm} type="datetime-local" />
                <FormRow label="Salida" field="customsTransitExit" form={detailForm} setForm={setDetailForm} type="datetime-local" />
              </div>
            </section>

            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Aduana destino exp.</h4>
              <div className="grid grid-cols-2 gap-3">
                <FormRow label="Prev. llegada" field="expectedCustomsDestination" form={detailForm} setForm={setDetailForm} type="datetime-local" />
                <FormRow label="Entrada" field="customsDestinationEntry" form={detailForm} setForm={setDetailForm} type="datetime-local" />
                <FormRow label="Salida" field="customsDestinationExit" form={detailForm} setForm={setDetailForm} type="datetime-local" />
              </div>
            </section>

            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Descarga</h4>
              <div className="grid grid-cols-2 gap-3">
                <FormRow label="Prev. llegada" field="expectedUnloadingArrival" form={detailForm} setForm={setDetailForm} type="datetime-local" />
                <FormRow label="Llegada descarga" field="unloadingArrival" form={detailForm} setForm={setDetailForm} type="datetime-local" />
                <FormRow label="Salida descarga" field="unloadingDeparture" form={detailForm} setForm={setDetailForm} type="datetime-local" />
              </div>
            </section>
          </div>
          <SheetFooter>
            <Button variant="outline" onClick={() => setEditingDetail(null)} disabled={updateDetailMut.isPending}>
              Cancelar
            </Button>
            <Button
              disabled={updateDetailMut.isPending}
              onClick={() => {
                if (!editingDetail) return
                const payload = buildDetailPayload(detailForm)
                updateDetailMut.mutate({ id: editingDetail.id, data: payload })
              }}
            >
              {updateDetailMut.isPending ? "Guardando..." : "Guardar"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* ── Export Preview Modal ──────────────────────────────────────── */}
      <Dialog open={!!exportPreview} onOpenChange={(open) => { if (!open) setExportPreview(null) }}>
        <DialogContent className="max-w-5xl w-full flex flex-col" style={{ maxHeight: "85vh" }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DownloadIcon className="size-4" />
              Preview de exportación
            </DialogTitle>
            <DialogDescription className="font-mono text-xs truncate">
              {exportPreview?.filename}
            </DialogDescription>
          </DialogHeader>

          {/* Download button */}
          <div className="flex items-center gap-2 pb-2 border-b">
            <Button size="sm" className="gap-2" onClick={handleDownloadPreview}>
              <DownloadIcon className="size-4" /> Descargar CSV
            </Button>
            <Button size="sm" variant="outline" className="gap-2" onClick={handleDownloadXlsPreview}>
              <DownloadIcon className="size-4" /> Descargar XLS (97–2003)
            </Button>
            <span className="text-xs text-muted-foreground">
              {exportPreview ? (() => {
                const lines = exportPreview.csvText.trim().split("\n")
                return `${lines.length - 1} fila${lines.length - 1 !== 1 ? "s" : ""}`
              })() : ""}
            </span>
          </div>

          {/* CSV table preview */}
          <div className="overflow-auto flex-1 rounded border">
            {exportPreview && (() => {
              const lines = exportPreview.csvText.trim().split("\n")
              const parseRow = (line: string) => {
                const cells: string[] = []
                let cur = "", inQ = false
                for (let i = 0; i < line.length; i++) {
                  const ch = line[i]
                  if (inQ) {
                    if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++ }
                    else if (ch === '"') { inQ = false }
                    else { cur += ch }
                  } else {
                    if (ch === '"') { inQ = true }
                    else if (ch === ",") { cells.push(cur); cur = "" }
                    else { cur += ch }
                  }
                }
                cells.push(cur)
                return cells
              }
              const headers = parseRow(lines[0] ?? "")
              const rows = lines.slice(1).map(parseRow)
              return (
                <table className="text-xs w-full border-collapse">
                  <thead className="sticky top-0 bg-muted z-10">
                    <tr>
                      {headers.map((h, i) => (
                        <th key={i} className="border-b border-r px-2 py-1.5 text-left font-semibold whitespace-nowrap text-muted-foreground last:border-r-0">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, ri) => (
                      <tr key={ri} className={ri % 2 === 0 ? "bg-background" : "bg-muted/30"}>
                        {row.map((cell, ci) => (
                          <td key={ci} className="border-b border-r px-2 py-1 whitespace-nowrap last:border-r-0 font-mono">
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            })()}
          </div>
        </DialogContent>
      </Dialog>

    </>
  )
}
